import React from "react"; // 182 C.1 — react/react-in-jsx-scope is counted per JSX element; the rule's own remedy (169, 168A)
import { useState, useEffect } from "react";
import type { Dispatch } from "react"; // 189 §B — a callback prop typed by an IMPORTED type (186's method)
import type { Translate } from "../lib/interestRefusal"; // 197B §C.2(c) — the translator typed at the seam, an imported type (no named-parameter function type)
import {
  Trash2,
  Plus,
  ChevronUp,
  ChevronDown,
  ListChecks,
} from "lucide-react";
import { api } from "../api";
import { isDividerEntry } from "../utils/taxonomyMap";
import { useTaxonomy, reloadAll } from "../hooks/useTaxonomy";
import { saveTaxonomyMeta, onTaxonomyMetaSettled } from "../lib/taxonomyMetaSaves"; // 207C §5 — what is typed is what is kept
import type { TaxonomyDeleteRefusal } from "../hooks/useTaxonomy"; // 181 B.2b
import { usePermissions } from "../hooks/usePermissions";
import ColorOrVarPicker, {
  THEME_PICKER_VARS,
  THEME_PICKER_BG_VARS,
} from "../components/ColorOrVarPicker";
import { useTranslation } from "../i18n";
import { iconByName, ICON_NAMES } from "../lib/lucideRegistry";
import { CT, RAD, FS } from "../lib/tokens";
import { ModalLayout, HScroll } from "../components/SharedUI"; // [X](b) — the strip's own scroll affordance
import { EISEN_QUADRANTS } from "../utils";
import { eisenLabel } from "../EisenBadge";
import { M365_IMPORT_ROLES, normaliseAlertLeadDays, normaliseSilenceDays } from "../utils/taxonomyMap";
// BRIEF 124 §1 — the mode vocabulary, from its one owner.
import { MEETING_MODES, asMeetingMode, type MeetingMode } from "../lib/meetingMode";
import { isTerminalStatus } from "../lib/taskCounters"; // brief 57 §1
import MenuSelect from "../components/MenuSelect"; // 154B §2
import Table from "../components/Table";
import type { TFn } from "../lib/taxonomyTypes"; // 189 §B — `t` narrowed through the imported type (185's method)
// 189 §B — the definition kinds' meta shapes, one home both ends read.
import {
  CUSTOM_FIELD_ENTITIES,
  CUSTOM_FIELD_TYPES,
  asCustomFieldType,
  customFieldDefsFor,
  customFieldLabel,
  normalizeBentoTemplateMeta, // 190 §C
  normalizeCustomFieldMeta,
  normalizePlaybookMeta,
  normalizeSegmentFilter,
  normalizeStageSetMeta,
  BENTO_SURFACES, // 190 §C
  BENTO_CASE_FIELDS, // 190 glance round 2 [C]
  PLAYBOOK_ASSIGNEES,
  PLAYBOOK_TRIGGERS,
} from "../lib/crmDefinitions.mjs";
import { resolveTaxonomyLabel } from "../lib/taxonomyHelpers"; // 189 §C — the one matcher (05m step C)
import type { TaxList } from "../lib/taxonomyTypes";

// Pick the right var list per meta-field name. Background-style fields
// (`bgColor`) get the alpha-shade variants so pills stay translucent;
// foreground fields (`color`, `iconColor`, `nameColor`) get the solid set.
function pickerVarsFor(fieldName: any) {
  return /bg/i.test(fieldName) ? THEME_PICKER_BG_VARS : THEME_PICKER_VARS;
}

// One-line human labels per kind. Ordering groups related kinds together.
// BRIEF 108 §4b — exported: the search dialog indexes these labels and lands
// on this card. The labels are this panel's own render vocabulary.
export const KIND_LABELS = [
  [
    "activity_code",
    "Activity Types (task + time entry codes, with subtask templates)",
  ],
  ["event_type", "Agenda Event Types (with subtask templates)"],
  ["case_type", "Case Types"],
  ["case_status", "Case Statuses"],
  ["case_stage", "Procedural Stage Types"],
  ["party_role", "Party Roles"],
  ["document_category", "Document Types"],
  ["document_status", "Document Statuses"],
  ["link_type", "Document Link Types"],
  ["external_link", "External Links"],
  ["task_status", "Task Statuses"],
  ["event_status", "Agenda Event Statuses"], // 136 §3
  ["item_level", "Levels"],
  ["reminder_option", "Reminder Options"],
  ["person_title", "Contact Person Titles"],
  ["client_type", "Client Types"],
  // 182 B.1 — the two CRM kinds: where a prospect came from, why one was lost.
  ["lead_source", "Lead Sources"],
  ["lost_reason", "Lost Reasons"],
  // 189 A.1 — where a marketing consent or refusal was recorded from.
  ["consent_source", "Consent Sources"],
  // 214 §4 — a client's contact's role: a plain contact, or the client's own counsel (Avocat).
  ["contact_role", "Contact Roles"],
  // 189 §B (167 R7) — a firm's own fields on clients, cases and collective
  // cases: the label is the row's, the type / list / entities / list-column
  // flag are its meta, edited in the row's own editor (the ListChecks toggle).
  ["custom_field", "Custom Fields"],
  // 189 §C — a named, ordered list of procedural stage types, applied to a
  // case from its form so it starts with the right stages.
  ["stage_set", "Stage Sets"],
  // 189 §D — when a matter is created or a stage is added, create these tasks.
  ["playbook", "Playbooks"],
  // 189 §H — a saved query over the firm's clients, the newsletter's list.
  ["mail_segment", "Mailing Segments"],
  ["bento_template", "Bento Templates"], // 190 §C
  ["bar", "Bar Associations"],
  ["country", "Countries"],
  ["invoice_payment_status", "Invoice Payment Statuses"],
  ["work_location", "Work Locations"],
  ["overhead_category", "Overhead Categories"], // 161 §9

  ["library_section", "Library Sections"],
  // Phase 19 — Places. place_category is the grid-split axis and is
  // REQUIRED on every Place row; the taxonomy DELETE handler enforces
  // reassign-only for this kind (see REASSIGN_ONLY_KINDS in
  // server/src/routes/taxonomies.js). place_location and place_state are
  // optional descriptors and accept null-on-delete.
  ["place_category", "Place categories"],
  ["place_location", "Place locations"],
  ["place_state", "Place states"],
];

// meta schema per kind — drives which extra columns show in the table and
// the add form. Colour fields render as text inputs so admins can type
// either CSS var names (`var(--gn)`) or hex literals — the var form lets
// the colour follow whichever theme the firm picks.
const META_FIELDS = {
  // `firmWide` — P20b. TRUE means the whole firm sees events of this type
  // regardless of case scope (the relocated REU carve-out, D10). A VISIBILITY
  // switch: ticking one opens that type's events to every collaborator.
  // `m365ImportRole` — P20c. Which type a meeting imported from Microsoft 365
  // lands on (virtual / in person). At most one type holds each role.
  // `alertLeadDays` — brief 46 §1. The days before an ALL-DAY event of this
  // type on which it alerts; empty means it does not, which is what every type
  // but `PRE` starts as. This is the field that lets a firm decide how much
  // warning a prescription deadline gets without a deploy.
  // `syncInTasksAndEvents` — BRIEF 87 §8b. Types carrying it are the ones whose
  // task/event stays LINKED to the case stage it was filed under: the fields
  // resolve from the stage on every read and are not editable on the row.
  // Laurent's words: "while diminishing friction, avoiding that for important
  // activity types the task/event recording could drift from the legal duel date
  // or event." AUD (hearings) and CCL (written statements) are the motivating
  // cases and NOT the rule — the flag is a taxonomy setting precisely so a firm
  // can decide which of its own types matter without a deploy.
  //
  // `isMeeting` — BRIEF 123 §1. WHETHER events of this type are meetings: they
  // carry an end time, a Physical/Online mode and an invitation list. ANY NUMBER
  // OF TYPES MAY SAY YES — a hearing held by videoconference is a meeting, and
  // before this flag it had no home at all, because the app read two hardcoded
  // Belgian codes (`EV_MEETING_KIND = { REU: "room", VIR: "virtual" }`, App.tsx:504@50fb417c) and so
  // asked "is this a meeting?" and "is it in person or online?" as one question.
  //
  // IT DOES NOT REPLACE `m365ImportRole` AND IS NOT MERGED INTO IT. TWO
  // QUESTIONS, TWO FIELDS:
  //   m365ImportRole  WHICH type an imported M365 meeting becomes. One row per
  //                   role, first in taxonomy order wins
  //                   (server/src/lib/eventTypeImport.js:28-38). A routing
  //                   answer, and at most two rows can give it.
  //   isMeeting       WHETHER a type's events are meetings. Any number of rows
  //                   may say yes, and a firm that never imports from M365
  //                   still needs it.
  // The seed's rule is stated as "rows carrying a role" rather than "REU and
  // VIR" (taxonomyMap.eventTypeMeta), which is the point of the whole brief: a
  // firm that renames REU keeps a working modal.
  // BRIEF 124 §1 — `defaultMode` sits immediately after `isMeeting`, because it
  // is only meaningful when that one is ticked and the column order is what
  // makes that legible without reading a tooltip.
  event_type: ["color", "firmWide", "m365ImportRole", "alertLeadDays", "syncInTasksAndEvents", "isMeeting", "defaultMode"],
  document_status: ["color"],
  item_level: ["color"],
  link_type: ["icon", "iconColor", "nameColor"],
  // Phase 14 — External resources page entries. logoData = optional
  // uploaded image as a base64 data URI (primary visual); icon = Lucide
  // name string fallback (resolved via src/lib/lucideRegistry.js);
  // url = the https target.
  external_link: ["logoData", "icon", "url"],
  // Brief 57 §1 — `terminal` says whether a task carrying this status is
  // OVER. It is the reason "how many tasks are open" is one derivation
  // (src/lib/taskCounters.ts) rather than two lists of codes in two files:
  // a firm that adds a seventh status decides here what it means, and
  // every surface — including surfaces not yet written — follows.
  task_status: ["color", "bgColor", "terminal"],
  // BRIEF 136 §3 — `icon` is the Lucide name of the glyph that REPLACES the day
  // counter on an event of this status. EMPTY IS MEANINGFUL AND IS THE SAFE
  // ANSWER: it means "draw the counter", which is `confirmed`'s own behaviour —
  // so a firm that adds a fifth status, or clears one of these cells, gets the
  // ordinary countdown rather than a wrong glyph. (136 §4 drew that against
  // `chat_message_type`, where absent fell through to MessageCircle for
  // everything; 137 §5 retired the kind and the contrast with it.)
  event_status: ["color", "bgColor", "icon"],
  case_status: ["color", "bgColor"],
  // Phase 8 B2 — admin can tune case_type pill text + background colors.
  // 182 C.1 (167 R8) — `silenceDays`: after this many days without a contact
  // on a matter of this type, the alert engine tells the team. Empty = never.
  case_type: ["color", "bgColor", "silenceDays"],
  // `eisen` — the code's default Eisenhower quadrant for new tasks (read by
  // eisenAuto in src/utils.ts). Unset means no default: the task lands in
  // "s". A user's own placement and Task.caseEisenhower still win over it.
  // 200 §E — `secretarial`: ticked, a collaborator whose role bills at zero
  // (the secretarial roles) may log time on this activity; the time-entry
  // modal restricts such a role to the ticked set (App's former SEC_TASKS
  // literal, now the firm's own data). Nothing ticked = no restriction.
  activity_code: ["color", "eisen", "syncInTasksAndEvents", "secretarial"],   // brief 87 §8b · 200 §E
  invoice_payment_status: ["color", "bgColor"],
  work_location: ["color"],
  // Phase 19 — Places taxonomies get a single colour swatch each. The
  // category drives the per-grid header tint; location/state are
  // descriptive chips on the per-place header.
  place_category: ["color"],
  place_location: ["color"],
  place_state: ["color"],
};

// The add form's own shape. Named because brief 46 §1's field is the first
// meta editor added since the `any` ratchet, and the rule is that new code
// takes a type rather than a new baseline (SESSION_RULES). Deliberately NOT
// applied to the `useState` above: widening that would retype every existing
// call site, which is a separate act from adding a field.
type TaxonomyAddForm = {
  code: string;
  label: string;
  labelFr: string;
  labelNl: string;
  meta: Record<string, unknown>;
};

// Phase M+7 / Phase 7 stage 3 — kinds whose entries can carry a subtask-
// template payload. meta.subtasks: [{labelEn, labelFr, labelNl, offsetDays},
// …] auto-injects on parent creation.
const TEMPLATE_ENABLED_KINDS = new Set(["activity_code", "event_type"]);

/* 189 §B — A KIND WHOSE ROWS CARRY A DEFINITION gets its own editor in the
   expandable row under the entry, opened by the same toggle the subtask
   templates use. The table's columns do not move (no META_FIELDS entry): a
   definition is edited whole, not cell by cell. The editor receives the row's
   normalised meta and hands back the whole meta to write. */
type KindEditorProps = Readonly<{
  meta: Record<string, unknown>;
  isAdmin: boolean;
  onChange: Dispatch<Record<string, unknown>>;
}>;
const KIND_EDITORS: Record<string, React.FC<KindEditorProps>> = {
  custom_field: CustomFieldDefEditor,
  stage_set: StageSetEditor,
  playbook: PlaybookEditor,
  mail_segment: SegmentEditor,
  bento_template: BentoTemplateEditor, // 190 §C
};

// BRIEF 108 §2a — the MF9 validForJunctions editor is RETIRED with the D3
// filter it drove (RolePicker offers every non-archived role on every
// junction now — Laurent's ruling, RULINGS.md 2026-08-20). A checkbox matrix
// editing a field nothing reads is a control that lies; "defaults-adjacent"
// relabelling was considered and rejected — it would conflate the field with
// meta.defaultForJunctions, which is a different fact (pre-selection at add
// time, untouched, and deliberately editor-less today). The
// meta.validForJunctions DATA stays untouched on existing rows; the
// taxonomies.junction.* seed rows stay seeded and unread, per D28.

export default function SettingsTaxonomies({ isAdmin: isAdminProp, toast, focusSeq }: any) {
  const { t, lang } = useTranslation();
  // Phase auth-c2 Stage 3 — Custom Lists grantee guard. The legacy `isAdmin`
  // prop gated every CRUD affordance inside the panel; with the new per-
  // section gate (settings.customLists.taxonomies) seeded TRUE for the
  // settingsBusiness-holding roles, a non-admin grantee must be able to
  // USE the panel, not just see a read-only view. `isAdmin` (internal
  // identifier) is now "is allowed to edit taxonomies": isAdmin overlay
  // OR holds the per-section key. Server-side /api/taxonomies has its
  // own auth (existing — see routes/taxonomies.js).
  const _txPerms = usePermissions();
  const isAdmin =
    !!isAdminProp ||
    !!_txPerms.viewer?.isAdmin ||
    _txPerms.can("settings.customLists.taxonomies") === true;
  const [kind, setKind] = useState("activity_code");
  const { entries, loading } = useTaxonomy(kind, { includeArchived: true });
  /* 207C §5 — WHAT IS TYPED IS WHAT IS KEPT (207B §5's rule), for every editor
     an entry opens: each write of an entry's meta goes through its own save
     queue (src/lib/taxonomyMetaSaves.ts — one PATCH in flight, the latest meta
     waiting), and what was written is laid OVER the entry here until the queue
     is idle and a list reloaded after its last answer holds it. The editors'
     fields were drawn from the reloaded list alone, so an answer, or the reload
     after it, put older text back under the caret and the keys typed meanwhile
     were written over the older meta. */
  const [typedMeta, setTypedMeta] = useState<Record<number, Record<string, unknown>>>({});
  useEffect(
    () =>
      onTaxonomyMetaSettled((id) => {
        setTypedMeta((p) => (id in p ? Object.fromEntries(Object.entries(p).filter(([k]) => Number(k) !== id)) : p));
      }),
    [],
  );
  const writeMeta = (id: number, meta: Record<string, unknown>) => {
    setTypedMeta((p) => ({ ...p, [id]: meta }));
    saveTaxonomyMeta(kind, id, meta).catch((err: unknown) => {
      if (toast) toast(t("taxonomies.update_failed", { fallback: "Update failed" }) + ": " + (err instanceof Error ? err.message : String(err)));
    });
  };
  // The row, read through what was typed: its meta, and the row handed to TaxonomyRow.
  const typedOf = (e: unknown) => {
    const row = e as { id: number; meta?: unknown };
    const typed = typedMeta[row.id] as Record<string, unknown> | undefined;
    const meta = typed ?? (row.meta && typeof row.meta === "object" ? (row.meta as Record<string, unknown>) : {});
    return { row, typed, meta };
  };
  const shownRow = (e: unknown): unknown => {
    const { row, typed } = typedOf(e);
    return typed ? { ...row, meta: typed } : e;
  };
  const writeMetaOf = (e: unknown, patch: Record<string, unknown>) => {
    const { row, meta } = typedOf(e);
    writeMeta(row.id, { ...meta, ...patch });
  };
  const [adding, setAdding] = useState(false);
  /* 190 C.6 — the row just created opens its own definition editor. Surfaced
     in the 189 glance: the five per-kind editors (KIND_EDITORS) open only via
     the ListChecks toggle on an EXISTING row, so with "0 entrées" no editor was
     reachable and a firm could not author its first custom field, stage set,
     playbook, segment — or Bento template. The created row's id is held here
     until its row mounts and reads it. */
  const [openEditorFor, setOpenEditorFor] = useState<number | null>(null);
  const [form, setForm] = useState<any>({
    code: "",
    label: "",
    labelFr: "",
    labelNl: "",
    meta: {},
  });
  // Phase 8 F1 — component-local collapsibility, default closed to match
  // the "all others collapsed" rule in SettingsView. Brief F2 finding:
  // no persistence (resets on navigation).
  const [collapsed, setCollapsed] = useState(true);
  /* BRIEF 108 §4b — the search's one-shot focus hint: a new focusSeq token
     means a result chose this card; open it (the scroll is SettingsView's,
     via the data-spane anchor below). */
  useEffect(() => {
    if (focusSeq) setCollapsed(false);
  }, [focusSeq]);
  const metaFields = META_FIELDS[kind as keyof typeof META_FIELDS] || [];
  // Phase 14 Stage 3 r3 — external_link entries get a server-generated code
  // (slugified from the English label); the CODE field is hidden from the
  // admin UI. Every other kind keeps the admin-supplied code.
  const hideCode = kind === "external_link";
  // Phase 15.1 — Long EN/FR/NL meta columns were added Phase 7 Stage 2 for
  // the item_level short-vs-long-label pattern. Only item_level uses
  // them today; every other kind renders three empty columns and squeezes
  // the label columns. Restrict to the only consumer.
  const showLongLabels = kind === "item_level";

  const resetForm = () =>
    setForm({ code: "", label: "", labelFr: "", labelNl: "", meta: {} });

  // Phase 2a — all four label-bearing fields are required so admins seed FR
  // and NL up front rather than leaving silent gaps to discover in the UI.
  // external_link: code is auto-generated, so it is not required here.
  const formComplete = !!(
    (hideCode || form.code.trim()) &&
    form.label.trim() &&
    form.labelFr.trim() &&
    form.labelNl.trim()
  );

  async function create() {
    const code = form.code.trim();
    const label = form.label.trim();
    const labelFr = form.labelFr.trim();
    const labelNl = form.labelNl.trim();
    if ((!hideCode && !code) || !label || !labelFr || !labelNl) {
      toast &&
        toast(
          t("taxonomies.all_labels_required", {
            fallback: "code + EN/FR/NL labels required",
          }),
        );
      return;
    }
    try {
      const created = (await api.post("/taxonomies", {
        kind,
        ...(hideCode ? {} : { code }),
        label,
        labelEn: label,
        labelFr,
        labelNl,
        meta: metaFields.length ? form.meta : null,
      })) as { id?: number } | null;
      resetForm();
      setAdding(false);
      // 190 C.6 — a kind with an editor opens it on the row it just made.
      setOpenEditorFor(kind in KIND_EDITORS && created && typeof created.id === "number" ? created.id : null);
      reloadAll();
    } catch (e: any) {
      toast &&
        toast(
          t("taxonomies.create_failed", { fallback: "Create failed" }) +
            ": " +
            e.message,
        );
    }
  }

  // BRIEF 108 §6b — a divider is an ordinary entry create: meta.divider only;
  // the server generates the reserved code and allows the empty label (the
  // heading is optional and editable in the row afterwards).
  async function createDivider() {
    try {
      await api.post("/taxonomies", { kind, meta: { divider: true } });
      reloadAll();
    } catch (e: any) {
      toast &&
        toast(
          t("taxonomies.create_failed", { fallback: "Create failed" }) +
            ": " +
            e.message,
        );
    }
  }

  async function update(id: any, patch: any) {
    // reloadAll (soft refetch, keeps stale data mounted while fresh data
    // arrives) instead of invalidateAll (which nulled the cache and
    // unmounted the SubtaskTemplateEditor mid-add). Patches are quick so
    // the brief stale window before refetch lands is invisible.
    try {
      await api.patch("/taxonomies/" + id, patch);
      reloadAll();
    } catch (e: any) {
      toast &&
        toast(
          t("taxonomies.update_failed", { fallback: "Update failed" }) +
            ": " +
            e.message,
        );
    }
  }

  const [deleteDlg, setDeleteDlg] = useState<any>(null);
  function openDelete(entry: any) {
    setDeleteDlg(entry);
  }
  async function confirmDelete(body: any) {
    if (!deleteDlg) return;
    try {
      await api.del("/taxonomies/" + deleteDlg.id, body);
      setDeleteDlg(null);
      reloadAll();
    } catch (e: any) {
      toast &&
        toast(
          e.message ||
            t("taxonomies.delete_failed", { fallback: "Delete failed" }),
        );
      throw e;
    }
  }

  async function reorder(idx: any, dir: any) {
    const swap = dir === "up" ? idx - 1 : idx + 1;
    if (swap < 0 || swap >= entries.length) return;
    // Swap-by-order failed when rows share an order value (external_link
    // was seeded with order=0 across the board, making swaps a no-op).
    // Compute the rearranged list and renumber every row whose array
    // position no longer matches its stored order.
    const arr = entries.slice();
    [arr[idx], arr[swap]] = [arr[swap], arr[idx]];
    const updates: any[] = [];
    arr.forEach((e: any, i: any) => {
      if (e.order !== i)
        updates.push(api.patch("/taxonomies/" + e.id, { order: i }));
    });
    try {
      await Promise.all(updates);
      // Phase 15.2 — reloadAll (soft refetch) instead of invalidateAll
      // (hard wipe). invalidateAll nulled the cache mid-refetch, unmounted
      // every TaxonomyRow, dropped focus from the clicked arrow to
      // document.body, and the browser auto-scrolled the empty section
      // to its top. reload keeps the (already-correctly-reordered) stale
      // rows mounted until fresh data lands. Same rationale as update().
      reloadAll();
    } catch (e: any) {
      toast &&
        toast(
          t("taxonomies.reorder_failed", { fallback: "Reorder failed" }) +
            ": " +
            e.message,
        );
    }
  }

  return (
    <div
      className={`detail-card settings-pane${collapsed ? " cl" : ""}`}
      style={{ order: 81 }}
      data-spane="taxonomies"
    >
      <h4
        className="settings-pane-title"
        style={{ margin: "0 0 6px" }}
        onClick={() => setCollapsed((v) => !v)}
      >
        <span className="settings-chevron">{collapsed ? "▶" : "▼"}</span>
        {t("taxonomies.title", { fallback: "Taxonomies" })}
      </h4>
      <p
        style={{
          fontSize: FS.s11,
          color: "var(--tm)",
          margin: "4px 0 10px",
          lineHeight: 1.5,
        }}
      >
        {t("taxonomies.description", {
          fallback:
            "Manage dropdown options used throughout the app. Changes are saved via the API and propagate to open views.",
        })}
      </p>
      <div
        style={{
          display: "flex",
          gap: 8,
          alignItems: "center",
          marginBottom: 10,
          flexWrap: "wrap",
        }}
      >
        <label style={{ fontSize: FS.s10, color: "var(--tm)" }}>
          {t("taxonomies.kind", { fallback: "Kind" })}
        </label>
        <MenuSelect
          className="field-input"
          value={kind}
          onChange={(v) => {
            setKind(v);
            setAdding(false);
            resetForm();
          }}
          style={{ padding: "3px 6px", fontSize: FS.s11, minWidth: 280 }}
          /* BRIEF 108 §6a — alphabetical AT RENDER, by the label the viewer
             actually sees (the translated one), locale-aware. The array
             stays the source and keeps its grouping comments. */
          options={KIND_LABELS.map(
            ([k, l]) => [k, String(t("taxonomies.kind." + k, { fallback: l }))] as const,
          ).sort((a, b) => a[1].localeCompare(b[1], lang))}
        />
        <span style={{ fontSize: FS.s9, color: "var(--tm)" }}>
          {loading
            ? t("common.loading", { fallback: "Loading…" })
            : (() => {
                /* 108 §6b — a divider is not an entry to the count. */
                const n = entries.filter((e: { code?: unknown; meta?: { divider?: unknown } | null }) => !isDividerEntry(e)).length;
                return t("taxonomies.entries_count", {
                  count: n,
                  fallback: `${n} entr${n === 1 ? "y" : "ies"}`,
                });
              })()}
        </span>
      </div>
      {/* 190 round 6 [X](b) — the bare `overflowX: "auto"` had no affordance,
          so the columns ran off the right edge silently; `HScroll` (SharedUI)
          is the wrapper that draws the left/right arrows on the 14-day strip
          Laurent pointed at (Dashboard.tsx) — copied, not invented. */}
      <HScroll>
        <Table
          style={{ width: "100%", borderCollapse: "collapse", fontSize: FS.s11 }}
        >
          <thead>
            <tr style={{ borderBottom: "1px solid var(--br)" }}>
              {!hideCode && <Th>{t("common.code", { fallback: "Code" })}</Th>}
              <Th>
                {t("taxonomies.col.label_en", { fallback: "Label (EN)" })}
              </Th>
              <Th>
                {t("taxonomies.col.label_fr", { fallback: "Libellé (FR)" })}
              </Th>
              <Th>
                {t("taxonomies.col.label_nl", { fallback: "Label (NL)" })}
              </Th>
              {showLongLabels && (
                <Th>
                  {t("taxonomies.col.long_label_en", { fallback: "Long (EN)" })}
                </Th>
              )}
              {showLongLabels && (
                <Th>
                  {t("taxonomies.col.long_label_fr", { fallback: "Long (FR)" })}
                </Th>
              )}
              {showLongLabels && (
                <Th>
                  {t("taxonomies.col.long_label_nl", { fallback: "Long (NL)" })}
                </Th>
              )}
              {metaFields.map((f: any) => (
                <Th key={f}>{metaFieldHeader(f, t)}</Th>
              ))}
              <Th style={{ textAlign: "center", width: 60 }}>
                {t("common.order", { fallback: "Order" })}
              </Th>
              <Th style={{ textAlign: "center", width: 70 }}>
                {t("common.archived", { fallback: "Archived" })}
              </Th>
              <Th style={{ width: 28 }}></Th>
            </tr>
          </thead>
          <tbody>
            {entries.map((e: any, i: any) => (
              <TaxonomyRow
                key={e.id}
                entry={shownRow(e) /* 207C §5 — what was typed, over the entry */}
                idx={i}
                count={entries.length}
                isAdmin={isAdmin}
                metaFields={metaFields}
                toast={toast}
                templateEnabled={TEMPLATE_ENABLED_KINDS.has(kind)}
                kindEditor={KIND_EDITORS[kind]}
                editorOpenOnMount={openEditorFor !== null && openEditorFor === (e as { id: number }).id}
                onMetaChange={(w: { id: number; meta: Record<string, unknown> }) => { writeMeta(w.id, w.meta); }}
                iconPreviewEnabled={kind === "external_link"}
                hideCode={hideCode}
                showLongLabels={showLongLabels}
                onLabelChange={(v: any) => update(e.id, { label: v, labelEn: v })}
                onLabelFrChange={(v: any) => update(e.id, { labelFr: v })}
                onLabelNlChange={(v: any) => update(e.id, { labelNl: v })}
                onLongLabelChange={(langKey: any, v: any) => {
                  writeMetaOf(e, { [langKey]: v });
                }}
                onMetaFieldChange={(f: any, v: any) => {
                  writeMetaOf(e, { [f]: v });
                }}
                onSubtasksChange={(arr: any) => {
                  writeMetaOf(e, { subtasks: arr });
                }}
                onArchiveToggle={() => update(e.id, { archived: !e.archived })}
                onReorderUp={() => reorder(i, "up")}
                onReorderDown={() => reorder(i, "down")}
                onDelete={() => openDelete(e)}
              />
            ))}
          </tbody>
        </Table>
      </HScroll>
      <div
        style={{
          marginTop: 10,
          borderTop: "1px solid var(--br)",
          paddingTop: 8,
        }}
      >
        {adding ? (
          <div
            style={{
              display: "flex",
              gap: 6,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            {!hideCode && (
              <input
                className="field-input"
                style={inputStyle(110)}
                placeholder={t("common.code", { fallback: "code" })}
                value={form.code}
                onChange={(e) =>
                  setForm((p: any) => ({ ...p, code: e.target.value }))
                }
              />
            )}
            <input
              className="field-input"
              style={{ ...inputStyle(), flex: 1, minWidth: 140 }}
              placeholder={t("taxonomies.label_en_placeholder", {
                fallback: "label (EN)",
              })}
              value={form.label}
              onChange={(e) =>
                setForm((p: any) => ({ ...p, label: e.target.value }))
              }
            />
            <input
              className="field-input"
              style={{ ...inputStyle(), flex: 1, minWidth: 140 }}
              placeholder={t("taxonomies.label_fr_placeholder", {
                fallback: "libellé (FR)",
              })}
              value={form.labelFr}
              onChange={(e) =>
                setForm((p: any) => ({ ...p, labelFr: e.target.value }))
              }
            />
            <input
              className="field-input"
              style={{ ...inputStyle(), flex: 1, minWidth: 140 }}
              placeholder={t("taxonomies.label_nl_placeholder", {
                fallback: "label (NL)",
              })}
              value={form.labelNl}
              onChange={(e) =>
                setForm((p: any) => ({ ...p, labelNl: e.target.value }))
              }
            />
            {metaFields.map((f: any) =>
              isColorField(f) ? (
                <ColorOrVarPicker
                  key={f}
                  value={form.meta[f] || CT.c_888888}
                  onChange={(v: any) =>
                    setForm((p: any) => ({ ...p, meta: { ...p.meta, [f]: v } }))
                  }
                  themeVars={pickerVarsFor(f)}
                  mode="both"
                  swatchSize={28}
                  label={f}
                />
              ) : f === "logoData" && kind === "external_link" ? (
                <LogoUploadField
                  key={f}
                  value={form.meta[f]}
                  iconName={form.meta.icon || ""}
                  onChange={(v: any) =>
                    setForm((p: any) => ({ ...p, meta: { ...p.meta, [f]: v } }))
                  }
                  disabled={!isAdmin}
                  toast={toast}
                />
              ) : f === "icon" && kind === "external_link" ? (
                <IconMetaField
                  key={f}
                  initialValue={form.meta[f] || ""}
                  onChange={(v: any) =>
                    setForm((p: any) => ({ ...p, meta: { ...p.meta, [f]: v } }))
                  }
                  disabled={!isAdmin}
                />
              ) : f === "eisen" ? (
                <EisenMetaSelect
                  key={f}
                  value={form.meta[f]}
                  onChange={(v: any) =>
                    setForm((p: any) => ({ ...p, meta: { ...p.meta, [f]: v } }))
                  }
                  disabled={!isAdmin}
                />
              ) : f === "firmWide" ? (
                <FirmWideMetaToggle
                  key={f}
                  value={form.meta[f]}
                  onChange={(v: any) =>
                    setForm((p: any) => ({ ...p, meta: { ...p.meta, [f]: v } }))
                  }
                  disabled={!isAdmin}
                />
              ) : f === "syncInTasksAndEvents" ? (
                <SyncStageMetaToggle
                  key={f}
                  value={form.meta[f]}
                  onChange={(v: boolean) =>
                    setForm((p: TaxonomyAddForm) => ({ ...p, meta: { ...p.meta, [f]: v } }))
                  }
                  disabled={!isAdmin}
                />
              ) : f === "secretarial" ? (
                <SecretarialMetaToggle
                  key={f}
                  value={metaRecord(form.meta)[f]}
                  onChange={(v: boolean) => {
                    setForm((p: TaxonomyAddForm) => ({ ...p, meta: { ...p.meta, [f]: v } }));
                  }}
                  disabled={!isAdmin}
                  t={t}
                />
              ) : f === "isMeeting" ? (
                <IsMeetingMetaToggle
                  key={f}
                  value={form.meta[f]}
                  onChange={(v: boolean) =>
                    setForm((p: TaxonomyAddForm) => ({ ...p, meta: { ...p.meta, [f]: v } }))
                  }
                  disabled={!isAdmin}
                />
              ) : f === "defaultMode" ? (
                /* BRIEF 124 §1 — OFFERED ONLY ON A MEETING ROW. Untick
                   `isMeeting` and the control goes; the stored value is left
                   alone rather than cleared, so re-ticking restores the
                   preference the admin had already expressed instead of
                   silently discarding it. */
                form.meta.isMeeting === true ? (
                  <DefaultModeMetaSelect
                    key={f}
                    value={form.meta[f]}
                    onChange={(v: MeetingMode | undefined) =>
                      setForm((p: TaxonomyAddForm) => ({ ...p, meta: { ...p.meta, [f]: v } }))
                    }
                    disabled={!isAdmin}
                  />
                ) : null
              ) : f === "terminal" ? (
                <TerminalMetaToggle
                  key={f}
                  code={form.code}
                  value={form.meta[f]}
                  onChange={(v: boolean) =>
                    setForm((p: TaxonomyAddForm) => ({
                      ...p,
                      meta: { ...p.meta, [f]: v },
                    }))
                  }
                  disabled={!isAdmin}
                />
              ) : f === "m365ImportRole" ? (
                <M365RoleSelect
                  key={f}
                  value={form.meta[f]}
                  onChange={(v: any) =>
                    setForm((p: any) => ({ ...p, meta: { ...p.meta, [f]: v } }))
                  }
                  disabled={!isAdmin}
                />
              ) : f === "alertLeadDays" ? (
                <AlertLeadDaysField
                  key={f}
                  initialValue={form.meta[f]}
                  onCommit={(v: number[] | undefined) =>
                    setForm((p: TaxonomyAddForm) => ({ ...p, meta: { ...p.meta, [f]: v } }))
                  }
                  disabled={!isAdmin}
                />
              ) : f === "silenceDays" ? (
                <SilenceDaysField
                  key={f}
                  initialValue={form.meta[f]}
                  onCommit={(v: number | undefined) => {
                    setForm((p: TaxonomyAddForm) => ({ ...p, meta: { ...p.meta, [f]: v } }));
                  }}
                  disabled={!isAdmin}
                />
              ) : (
                <input
                  key={f}
                  className="field-input"
                  style={inputStyle(100)}
                  placeholder={f}
                  value={form.meta[f] || ""}
                  onChange={(e) =>
                    setForm((p: any) => ({
                      ...p,
                      meta: { ...p.meta, [f]: e.target.value },
                    }))
                  }
                />
              ),
            )}
            <button
              className="btn btn-primary"
              style={btnStyle}
              onClick={create}
              disabled={!isAdmin || !formComplete}
              title={
                !formComplete
                  ? t("taxonomies.all_labels_required", {
                      fallback: "code + EN/FR/NL labels required",
                    })
                  : ""
              }
            >
              {t("common.add", { fallback: "Add" })}
            </button>
            <button
              className="btn btn-danger"
              style={btnStyle}
              onClick={() => {
                setAdding(false);
                resetForm();
              }}
            >
              {t("common.cancel", { fallback: "Cancel" })}
            </button>
          </div>
        ) : (
          /* 109 §3 — the two adders ride ONE flex row: each button is its own
             display:flex (block-level), so as bare siblings they stacked. Add
             entry stays the primary; Add divider is its secondary sibling with
             the same Plus-and-label anatomy. */
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <button
              className="btn btn-primary"
              style={{
                ...btnStyle,
                display: "flex",
                alignItems: "center",
                gap: 3,
              }}
              onClick={() => setAdding(true)}
              disabled={!isAdmin}
              title={
                isAdmin ? "" : t("common.admin_only", { fallback: "Admin only" })
              }
            >
              <Plus size={12} />{" "}
              {/* 190 round 9 [AR] [RULED, Laurent 2026-09-17]: the add button
                  names what it adds — on Modèles de Bento "+ Ajouter un modèle
                  de Bento". PER-KIND LABEL WITH THE GENERIC AS FALLBACK — the
                  advisor's call: `taxonomies.add_entry.<kind>` looked up by
                  kind, falling back to `taxonomies.add_entry` where no row is
                  seeded (the resolver renders `fallback` when a key misses in
                  the active language and in English), so the next kind wanting
                  its own words is a seed row and not a second mechanism. Only
                  the Bento one is seeded. A computed key: the i18n census
                  names it under --computed rather than resolving it. */}
              {(t as TFn)("taxonomies.add_entry." + kind, { fallback: (t as TFn)("taxonomies.add_entry", { fallback: "Add entry" }) })}
            </button>
            {/* 110 §3b — the app's add affordance, same as "+ Add entry"
                beside it. A secondary-add look was the sanctioned alternative
                and does not exist to take: every add in this app is the
                light-blue `btn-primary` (censused — no Plus rides any other
                class), so matching means joining it, not inventing a tier. */}
            <button
              className="btn btn-primary"
              style={{ ...btnStyle, display: "flex", alignItems: "center", gap: 3 }}
              onClick={createDivider}
              disabled={!isAdmin}
              title={
                isAdmin ? "" : t("common.admin_only", { fallback: "Admin only" })
              }
            >
              <Plus size={12} />{" "}
              {t("taxonomies.add_divider", { fallback: "Add divider" })}
            </button>
          </div>
        )}
      </div>
      {deleteDlg && (
        <DeleteTaxonomyDialog
          entry={deleteDlg}
          siblings={entries.filter((e: any) => e.id !== deleteDlg.id && !e.archived)}
          onCancel={() => setDeleteDlg(null)}
          onConfirm={confirmDelete}
        />
      )}
    </div>
  );
}

/* 197B §C.2(c) — the one sentence of a delete the server would refuse and no
   sibling can rescue: referenced rows on a NOT NULL column, nothing to reassign
   to. Hookless at module scope (the dialog is near its complexity ceiling). */
function ReassignOnlyNoSibling({ t, refCount }: Readonly<{ t: Translate; refCount: number }>) {
  return (
    <>
      <p style={{ fontSize: FS.s12, color: "var(--rd)", fontWeight: 600, marginBottom: 8 }}>
        {t("taxonomies.delete.reassign_only_no_sibling", {
          fallback: "This entry is in use and cannot be left unclassified: it can only be deleted once another entry exists in this list to take over its references.",
        })}
      </p>
      <p style={{ fontSize: FS.s12, color: "var(--tm)" }}>
        {t("taxonomies.delete.refs_count", {
          count: refCount,
          fallback: "Currently referenced on " + String(refCount) + " row" + (refCount === 1 ? "" : "s") + ".",
        })}
      </p>
    </>
  );
}

/* 197B-bis §B.5 — the one sentence of a CASCADE: deleting the entry removes the
   cards' placements and nothing else; the cards return to the first column, where
   unplaced cards live (the editor's own help text). Hookless at module scope. */
function CascadeNote({ t }: Readonly<{ t: Translate }>) {
  return (
    <p style={{ fontSize: FS.s12, color: "var(--ts)", marginTop: 8 }}>
      {t("taxonomies.delete.cascade_note", {
        fallback: "Deleting it removes the cards' placements on this Bento: they return to the first column. Nothing else is removed.",
      })}
    </p>
  );
}

/** 197B-bis §B.4 — what deleting an entry of this kind does to the rows that
 *  reference it, as the server declares it (lib/taxonomyClassification.js). */
type DeleteShape = "null" | "reassign" | "cascade";

function DeleteTaxonomyDialog({ entry, siblings, onCancel, onConfirm }: any) {
  const { t } = useTranslation();
  const [refCount, setRefCount] = useState<any>(null);
  /* 197B-bis §B.4 — served with the count: THE DELETE SHAPE, so this dialog
     branches on what the server will DO — set null (both radios), reassign
     (that radio alone, or no delete without a sibling), cascade (no radio at
     all, the sentence, Delete offered). Replaces 197B's boolean `nullPermitted`. */
  const [deleteShape, setDeleteShape] = useState<DeleteShape>("null");
  // 05l §3 — the server's refusal, fetched WITH the count so the dialog can say
  // why up front instead of offering a button that 409s. Typed rather than
  // `any`: the shape is `deleteRefusal`'s return in
  // server/src/lib/taxonomyClassification.js, and the ratchet does not take a
  // new baseline for new code.
  type Refusal = { code: string; kind: string; entryCode: string };
  const [refusal, setRefusal] = useState<Refusal | null>(null);
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState("null");
  const [reassignTo, setReassignTo] = useState(siblings[0]?.code || "");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (
      api.get("/taxonomies/" + entry.id + "/reference-count") as Promise<{
        referenceCount?: number;
        refusal?: Refusal | null;
        deleteShape?: DeleteShape;
      }>
    )
      .then((data) => {
        if (!cancelled) {
          setRefCount(data.referenceCount || 0);
          setRefusal(data.refusal || null);
          setDeleteShape(data.deleteShape === "reassign" || data.deleteShape === "cascade" ? data.deleteShape : "null");
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setRefCount(0);
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [entry.id]);

  const noSiblings = siblings.length === 0;
  /* 197B §C.2(c) — A REFUSED DELETE OFFERS NO DELETE (05o §1, this dialog's own
     rule): with referencing rows and null not permitted, reassign is the ONLY
     act — the « Leave unclassified » radio is not rendered — and with no
     sibling to reassign to there is no Delete at all, only the sentence. */
  const reassignOnly = refCount > 0 && deleteShape === "reassign";
  const cannotDelete = reassignOnly && noSiblings;
  /* 197B-bis §B.5 — a CASCADE with references: nothing to choose (no radio),
     the count line stays (it is true), the sentence says what deleting does,
     Delete is offered; the « no sibling » arm above is unreachable here by
     construction — it hangs on `reassignOnly`, which a cascade never is. */
  const cascade = refCount > 0 && deleteShape === "cascade";
  let effectiveAction = action;
  if (reassignOnly) effectiveAction = "reassign";
  else if (noSiblings || cascade) effectiveAction = "null";
  const label = entry.labelEn || entry.label || entry.code;

  async function submit() {
    setSubmitting(true);
    try {
      const body =
        refCount && refCount > 0
          ? {
              action: effectiveAction,
              ...(effectiveAction === "reassign" ? { reassignTo } : {}),
            }
          : { action: "null" };
      await onConfirm(body);
    } catch {
      setSubmitting(false);
    }
  }

  return (
    <ModalLayout
      title={t("taxonomies.delete.title", {
        fallback: 'Delete "' + label + '"?',
        name: label,
      })}
      onClose={onCancel}
      showX={false}
      closeOnOverlay
      width={460}
      footer={
        /* 05o §1 — A REFUSED DELETE OFFERS NO DELETE. The button used to render
           in full danger styling and be `disabled`, which reads as a broken app
           rather than a protected entry: `.modal-delete` has no `:disabled`
           rule, so it still recoloured on hover and then ignored the click.
           A disabled control that looks enabled is worse than either honest
           state, so the affordance is ABSENT and the dialogue keeps its real
           job — saying WHY. `.modal-delete:disabled` now exists too (App.css),
           for the delete that is merely waiting on a reassign target. */
        <div className="modal-footer">
          {refusal || cannotDelete ? (
            <button className="modal-cancel" onClick={onCancel}>
              {t("common.close", { fallback: "Close" })}
            </button>
          ) : (
            <>
              <button
                className="modal-delete"
                onClick={submit}
                disabled={
                  loading ||
                  submitting ||
                  (refCount > 0 && effectiveAction === "reassign" && !reassignTo)
                }
              >
                {submitting
                  ? t("common.saving", { fallback: "Saving…" })
                  : t("common.delete", { fallback: "Delete" })}
              </button>
              <button className="modal-cancel" onClick={onCancel} disabled={submitting}>
                {t("common.cancel", { fallback: "Cancel" })}
              </button>
            </>
          )}
        </div>
      }
    >
          {loading ? (
            <p style={{ fontSize: FS.s12, color: "var(--tm)" }}>
              {t("common.loading", { fallback: "Loading…" })}
            </p>
          ) : refusal ? (
            /* 05l §3 — FIXED and protected codes refuse, and the refusal says why
               in the admin's language. The reference count is still shown: an
               admin who cannot delete still wants to know what uses it. */
            <>
              <p style={{ fontSize: FS.s12, color: "var(--rd)", fontWeight: 600, marginBottom: 8 }}>
                {refusal.code === "taxonomy.delete_refused_fixed"
                  ? t("taxonomies.delete.refused_fixed", {
                      code: entry.code,
                      fallback:
                        'This list is part of how the application works — "' +
                        entry.code +
                        '" cannot be deleted.',
                    })
                  : t("taxonomies.delete.refused_protected", {
                      code: entry.code,
                      fallback:
                        '"' +
                        entry.code +
                        '" cannot be deleted: the application branches on this code. You can still add and remove your own entries in this list.',
                    })}
              </p>
              <p style={{ fontSize: FS.s12, color: "var(--tm)" }}>
                {refCount > 0
                  ? t("taxonomies.delete.refs_count", {
                      count: refCount,
                      fallback:
                        "Currently referenced on " +
                        refCount +
                        " row" +
                        (refCount === 1 ? "" : "s") +
                        ".",
                    })
                  : t("taxonomies.delete.no_refs", {
                      fallback: "No rows reference this entry.",
                    })}
              </p>
            </>
          ) : (
            <>
              {/* 197B §C.2(c) — the delete no sibling can rescue: the sentence, no form */}
              {cannotDelete && <ReassignOnlyNoSibling t={t as Translate} refCount={Number(refCount)} />}
              {!cannotDelete && (
                <p
                  style={{
                    fontSize: FS.s12,
                    color: "var(--ts)",
                    marginBottom: refCount > 0 ? 12 : 0,
                  }}
                >
                  {refCount > 0
                    ? t("taxonomies.delete.refs_count", {
                        count: refCount,
                        fallback:
                          "Currently referenced on " +
                          refCount +
                          " row" +
                          (refCount === 1 ? "" : "s") +
                          ".",
                      })
                    : t("taxonomies.delete.no_refs", {
                        fallback: "No rows reference this entry.",
                      })}
                </p>
              )}
              {cascade && <CascadeNote t={t as Translate} />}
              {!cannotDelete && !cascade && refCount > 0 && (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 8,
                    padding: 8,
                    background: "var(--bi)",
                    borderRadius: RAD.r6,
                    border: "1px solid var(--br)",
                  }}
                >
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      fontSize: FS.s12,
                      color: noSiblings ? "var(--tm)" : "var(--tp)",
                      cursor: noSiblings ? "not-allowed" : "pointer",
                    }}
                    title={
                      noSiblings
                        ? t("taxonomies.delete.no_siblings", {
                            fallback: "No other entries to reassign to",
                          })
                        : ""
                    }
                  >
                    <input
                      type="radio"
                      name="dx-tax-action"
                      value="reassign"
                      checked={effectiveAction === "reassign"}
                      onChange={() => !noSiblings && setAction("reassign")}
                      disabled={noSiblings}
                    />
                    <span>
                      {t("taxonomies.delete.reassign_to", {
                        fallback: "Reassign references to:",
                      })}
                    </span>
                    <MenuSelect
                      className="field-input"
                      style={{
                        flex: 1,
                        padding: "2px 6px",
                        fontSize: FS.s11,
                        minWidth: 0,
                      }}
                      value={reassignTo}
                      onChange={(v) => setReassignTo(v)}
                      disabled={noSiblings || effectiveAction !== "reassign"}
                      options={[
                        ...(noSiblings ? [["", "—"] as const] : []),
                        ...siblings.map(
                          (sb: any) =>
                            [
                              String(sb.code),
                              (sb.labelEn || sb.label || sb.code) +
                                " (" +
                                sb.code +
                                ")",
                            ] as const,
                        ),
                      ]}
                    />
                  </label>
                  {/* 197B §C.2(c) — never offered where the server would refuse it */}
                  {!reassignOnly && (
                    <label
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        fontSize: FS.s12,
                        color: "var(--tp)",
                        cursor: "pointer",
                      }}
                    >
                      <input
                        type="radio"
                        name="dx-tax-action"
                        value="null"
                        checked={effectiveAction === "null"}
                        onChange={() => setAction("null")}
                      />
                      <span>
                        {t("taxonomies.delete.set_null", {
                          fallback: "Leave unclassified (set to null)",
                        })}
                      </span>
                    </label>
                  )}
                </div>
              )}
            </>
          )}
    </ModalLayout>
  );
}

function Th({ children, style }: any) {
  return (
    <th
      style={{
        textAlign: "left",
        padding: "4px 6px",
        fontSize: FS.s9,
        color: "var(--tm)",
        textTransform: "uppercase",
        letterSpacing: ".4px",
        fontWeight: 600,
        ...(style || {}),
      }}
    >
      {children}
    </th>
  );
}

function TaxonomyRow({
  entry,
  idx,
  count,
  isAdmin,
  metaFields,
  toast,
  templateEnabled,
  kindEditor,
  editorOpenOnMount,
  onMetaChange,
  iconPreviewEnabled,
  hideCode,
  showLongLabels,
  onLabelChange,
  onLabelFrChange,
  onLabelNlChange,
  onLongLabelChange,
  onMetaFieldChange,
  onSubtasksChange,
  onArchiveToggle,
  onReorderUp,
  onReorderDown,
  onDelete,
}: any) {
  // 181 B.2b — `t` narrowed at this row's seam (InvoiceTable.tsx:137's
  // pattern), so the bin's sentences are typed calls; the served refusal and
  // the code read through a typed view of the row (the row is `any` here and
  // the eslint ratchet counts every unsafe read).
  const { t } = useTranslation() as { t: (key: string, vars?: Record<string, unknown>) => string };
  // 190 C.6 — a row created a moment ago mounts with its editor open.
  // 190 [H] (2026-09-15) opened every editor-bearing row on mount; 190
  // round 5 [P] (ruled 2026-09-16) REVERSES it: all five kinds mount CLOSED
  // (the measured page length: 155–463 visual px per open row), the toggle
  // — now visible, not red — opens one. A row created a moment ago still
  // opens (C.6).
  const [showTpl, setShowTpl] = useState(editorOpenOnMount === true);
  const refusal: TaxonomyDeleteRefusal | null =
    (entry as { refusal?: TaxonomyDeleteRefusal | null }).refusal ?? null;
  const entryCode = String((entry as { code?: unknown }).code ?? "");
  let binTitle: string;
  if (!isAdmin) {
    binTitle = t("common.admin_only", { fallback: "Admin only" });
  } else if (refusal && refusal.code === "taxonomy.delete_refused_fixed") {
    binTitle = t("taxonomies.delete.refused_fixed", {
      code: entryCode,
      fallback: 'This list is part of how the application works — "' + entryCode + '" cannot be deleted.',
    });
  } else if (refusal) {
    binTitle = t("taxonomies.delete.refused_protected", {
      code: entryCode,
      fallback: '"' + entryCode + '" cannot be deleted: the application branches on this code. You can still add and remove your own entries in this list.',
    });
  } else {
    binTitle = t("taxonomies.delete_tooltip", { fallback: "Delete entry — references will be reassigned or cleared" });
  }
  const subtasks = Array.isArray(entry.meta?.subtasks)
    ? entry.meta.subtasks
    : [];
  /* 189 §B — the definition editor arrives through the `any` props bag; one
     cast at the seam (185's method) and the ratchet does not move.
     HOTFIX 2026-09-15 — IT IS A COMPONENT AND IS RENDERED AS ONE, `<Editor
     … />`, never CALLED: the call form ran the editor's body inside THIS
     row's render, so its hooks (every one of the four 189 editors carries
     useState / useTaxonomy / useTranslation) joined the ROW's hook list the
     moment the toggle opened — "Rendered more hooks than during the previous
     render", React #310, and the whole tree unmounted to a black screen.
     MEASURED on staging: Custom fields, one row, the toggle → `#root` with
     zero children and the #310 page error. The element form gives the editor
     its own fiber, where its hooks belong. */
  const Editor = kindEditor as React.FC<KindEditorProps> | undefined;
  const metaChange = onMetaChange as Dispatch<{ id: number; meta: Record<string, unknown> }>;
  const entryId = (entry as { id: number }).id;
  // 2 extra columns added in Phase 2a (FR + NL); 3 more added Phase 7
  // Stage 2 (longLabel EN/FR/NL — meta-stored, generic across all kinds).
  // The code column is dropped for external_link (hideCode); Long EN/FR/NL
  // are dropped for every kind except item_level (Phase 15.1).
  const colspan =
    (hideCode ? 9 : 10) - (showLongLabels ? 0 : 3) + metaFields.length;
  /* BRIEF 108 §6b — a divider renders as a rule with an optional, editable
     heading; the EXISTING reorder arrows, archive box and delete work on it
     exactly as on an item row (same handlers, same tail cells). Template and
     junction tools do not apply, so its actions cell carries delete alone. */
  if (isDividerEntry(entry)) {
    const midSpan = colspan - (hideCode ? 0 : 1) - 3;
    return (
      <tr
        style={{
          borderBottom: "1px solid var(--br)",
          opacity: entry.archived ? 0.55 : 1,
          background: "var(--bg2)",
        }}
      >
        {!hideCode && (
          <td style={tdStyle}>
            <span style={{ color: "var(--tm)", fontSize: FS.s10 }}>──</span>
          </td>
        )}
        <td colSpan={midSpan} style={tdStyle}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ flex: 1, borderTop: "2px solid var(--br)" }} />
            {isAdmin ? (
              <input
                className="field-input"
                style={{ ...inputStyle(), maxWidth: 220 }}
                placeholder={t("taxonomies.divider_heading_ph", {
                  fallback: "(optional heading)",
                })}
                defaultValue={entry.label || ""}
                onBlur={(e) => {
                  const v = e.target.value;
                  if (v !== (entry.label || "")) onLabelChange(v);
                }}
              />
            ) : entry.label ? (
              <span style={{ color: "var(--tm)", fontSize: FS.s10 }}>
                {entry.label}
              </span>
            ) : null}
            <span style={{ flex: 1, borderTop: "2px solid var(--br)" }} />
          </div>
        </td>
        <td style={{ ...tdStyle, textAlign: "center" }}>
          <button
            style={chevStyle(idx === 0 || !isAdmin)}
            onClick={onReorderUp}
            disabled={idx === 0 || !isAdmin}
          >
            <ChevronUp size={12} />
          </button>
          <button
            style={chevStyle(idx === count - 1 || !isAdmin)}
            onClick={onReorderDown}
            disabled={idx === count - 1 || !isAdmin}
          >
            <ChevronDown size={12} />
          </button>
        </td>
        <td style={{ ...tdStyle, textAlign: "center" }}>
          <input
            type="checkbox"
            checked={!!entry.archived}
            onChange={onArchiveToggle}
            disabled={!isAdmin}
          />
        </td>
        <td style={{ ...tdStyle, textAlign: "right", whiteSpace: "nowrap" }}>
          <button
            className="btn btn-danger"
            style={{
              padding: "1px 4px",
              fontSize: FS.s9,
              opacity: !isAdmin ? 0.4 : 1,
              cursor: !isAdmin ? "not-allowed" : "pointer",
            }}
            onClick={onDelete}
            disabled={!isAdmin}
            title={t("common.delete", { fallback: "Delete" })}
          >
            <Trash2 size={11} />
          </button>
        </td>
      </tr>
    );
  }
  return (
    <>
      <tr
        style={{
          borderBottom: "1px solid var(--br)",
          opacity: entry.archived ? 0.55 : 1,
        }}
      >
        {!hideCode && (
          <td style={tdStyle}>
            <span
              style={{
                fontFamily: "'JetBrains Mono',monospace",
                fontSize: FS.s10,
                color: "var(--ts)",
              }}
            >
              {entry.code}
            </span>
          </td>
        )}
        <td style={tdStyle}>
          {isAdmin ? (
            <input
              className="field-input"
              style={inputStyle()}
              defaultValue={entry.label}
              onBlur={(e) => {
                const v = e.target.value;
                if (v !== entry.label && v.trim()) onLabelChange(v.trim());
              }}
            />
          ) : (
            entry.label
          )}
          {templateEnabled && subtasks.length > 0 && (
            <span
              title={`${subtasks.length} subtask template${subtasks.length === 1 ? "" : "s"}`}
              style={{
                marginLeft: 6,
                fontSize: FS.s9,
                padding: "1px 5px",
                borderRadius: RAD.r8,
                background: "var(--bg2)",
                color: "var(--tm)",
              }}
            >
              {subtasks.length}
            </span>
          )}
        </td>
        <td style={tdStyle}>
          {isAdmin ? (
            <input
              className="field-input"
              style={inputStyle()}
              defaultValue={entry.labelFr || ""}
              placeholder={t("taxonomies.label_fr_placeholder", {
                fallback: "libellé (FR)",
              })}
              onBlur={(e) => {
                const v = e.target.value.trim();
                if (v && v !== (entry.labelFr || "")) onLabelFrChange(v);
              }}
            />
          ) : (
            entry.labelFr || "—"
          )}
        </td>
        <td style={tdStyle}>
          {isAdmin ? (
            <input
              className="field-input"
              style={inputStyle()}
              defaultValue={entry.labelNl || ""}
              placeholder={t("taxonomies.label_nl_placeholder", {
                fallback: "label (NL)",
              })}
              onBlur={(e) => {
                const v = e.target.value.trim();
                if (v && v !== (entry.labelNl || "")) onLabelNlChange(v);
              }}
            />
          ) : (
            entry.labelNl || "—"
          )}
        </td>
        {showLongLabels && (
          <td style={tdStyle}>
            {isAdmin ? (
              <input
                className="field-input"
                style={inputStyle()}
                defaultValue={(entry.meta && entry.meta.longLabelEn) || ""}
                placeholder={t("taxonomies.long_label_en_placeholder", {
                  fallback: "long label (EN)",
                })}
                onBlur={(e) => {
                  const v = e.target.value.trim();
                  if (v !== ((entry.meta && entry.meta.longLabelEn) || ""))
                    onLongLabelChange("longLabelEn", v);
                }}
              />
            ) : (
              (entry.meta && entry.meta.longLabelEn) || "—"
            )}
          </td>
        )}
        {showLongLabels && (
          <td style={tdStyle}>
            {isAdmin ? (
              <input
                className="field-input"
                style={inputStyle()}
                defaultValue={(entry.meta && entry.meta.longLabelFr) || ""}
                placeholder={t("taxonomies.long_label_fr_placeholder", {
                  fallback: "libellé long (FR)",
                })}
                onBlur={(e) => {
                  const v = e.target.value.trim();
                  if (v !== ((entry.meta && entry.meta.longLabelFr) || ""))
                    onLongLabelChange("longLabelFr", v);
                }}
              />
            ) : (
              (entry.meta && entry.meta.longLabelFr) || "—"
            )}
          </td>
        )}
        {showLongLabels && (
          <td style={tdStyle}>
            {isAdmin ? (
              <input
                className="field-input"
                style={inputStyle()}
                defaultValue={(entry.meta && entry.meta.longLabelNl) || ""}
                placeholder={t("taxonomies.long_label_nl_placeholder", {
                  fallback: "lang label (NL)",
                })}
                onBlur={(e) => {
                  const v = e.target.value.trim();
                  if (v !== ((entry.meta && entry.meta.longLabelNl) || ""))
                    onLongLabelChange("longLabelNl", v);
                }}
              />
            ) : (
              (entry.meta && entry.meta.longLabelNl) || "—"
            )}
          </td>
        )}
        {metaFields.map((f: any) => (
          <td key={f} style={tdStyle}>
            {isColorField(f) ? (
              <ColorOrVarPicker
                value={(entry.meta && entry.meta[f]) || CT.c_888888}
                onChange={(v: any) => onMetaFieldChange(f, v)}
                themeVars={pickerVarsFor(f)}
                mode="both"
                swatchSize={24}
                disabled={!isAdmin}
              />
            ) : f === "logoData" && iconPreviewEnabled ? (
              <LogoUploadField
                value={entry.meta && entry.meta[f]}
                iconName={(entry.meta && entry.meta.icon) || ""}
                onChange={(v: any) => onMetaFieldChange(f, v)}
                disabled={!isAdmin}
                toast={toast}
              />
            ) : f === "icon" && iconPreviewEnabled ? (
              <IconMetaField
                initialValue={(entry.meta && entry.meta[f]) || ""}
                onCommit={(v: any) => {
                  if (v !== (entry.meta?.[f] || "")) onMetaFieldChange(f, v);
                }}
                disabled={!isAdmin}
                width={110}
              />
            ) : f === "eisen" ? (
              <EisenMetaSelect
                value={entry.meta && entry.meta[f]}
                onChange={(v: any) => onMetaFieldChange(f, v)}
                disabled={!isAdmin}
              />
            ) : f === "firmWide" ? (
              <FirmWideMetaToggle
                value={entry.meta && entry.meta[f]}
                onChange={(v: any) => onMetaFieldChange(f, v)}
                disabled={!isAdmin}
              />
            ) : f === "syncInTasksAndEvents" ? (
              <SyncStageMetaToggle
                value={entry.meta && entry.meta[f]}
                onChange={(v: boolean) => onMetaFieldChange(f, v)}
                disabled={!isAdmin}
              />
            ) : f === "secretarial" ? (
              <SecretarialMetaToggle
                value={metaRecord(entry.meta)[f]}
                onChange={(v: boolean) => { onMetaFieldChange(f, v); }}
                disabled={!isAdmin}
                t={t}
              />
            ) : f === "isMeeting" ? (
              <IsMeetingMetaToggle
                value={entry.meta && entry.meta[f]}
                onChange={(v: boolean) => onMetaFieldChange(f, v)}
                disabled={!isAdmin}
              />
            ) : f === "defaultMode" ? (
              /* BRIEF 124 §1 — the column exists on every event_type row; the
                 CONTROL exists only where `isMeeting` is true. A dash on the
                 others says "this question does not apply here", which is the
                 honest reading and the one an empty cell would not give. */
              entry.meta && entry.meta.isMeeting === true ? (
                <DefaultModeMetaSelect
                  value={entry.meta && entry.meta[f]}
                  onChange={(v: MeetingMode | undefined) => onMetaFieldChange(f, v)}
                  disabled={!isAdmin}
                />
              ) : (
                "—"
              )
            ) : f === "terminal" ? (
              <TerminalMetaToggle
                code={entry.code}
                value={entry.meta && entry.meta[f]}
                onChange={(v: boolean) => onMetaFieldChange(f, v)}
                disabled={!isAdmin}
              />
            ) : f === "m365ImportRole" ? (
              <M365RoleSelect
                value={entry.meta && entry.meta[f]}
                onChange={(v: any) => onMetaFieldChange(f, v)}
                disabled={!isAdmin}
              />
            ) : f === "alertLeadDays" ? (
              <AlertLeadDaysField
                initialValue={entry.meta && entry.meta[f]}
                onCommit={(v: number[] | undefined) => onMetaFieldChange(f, v)}
                disabled={!isAdmin}
                width={110}
              />
            ) : f === "silenceDays" ? (
              <SilenceDaysField
                initialValue={entry.meta && entry.meta[f]}
                onCommit={(v: number | undefined) => {
                  onMetaFieldChange(f, v);
                }}
                disabled={!isAdmin}
                width={90}
              />
            ) : (
              <input
                className="field-input"
                style={inputStyle(90)}
                defaultValue={(entry.meta && entry.meta[f]) || ""}
                onBlur={(e) => {
                  const v = e.target.value;
                  if (v !== (entry.meta?.[f] || "")) onMetaFieldChange(f, v);
                }}
                disabled={!isAdmin}
              />
            )}
          </td>
        ))}
        <td style={{ ...tdStyle, textAlign: "center" }}>
          <button
            style={chevStyle(idx === 0 || !isAdmin)}
            onClick={onReorderUp}
            disabled={idx === 0 || !isAdmin}
          >
            <ChevronUp size={12} />
          </button>
          <button
            style={chevStyle(idx === count - 1 || !isAdmin)}
            onClick={onReorderDown}
            disabled={idx === count - 1 || !isAdmin}
          >
            <ChevronDown size={12} />
          </button>
        </td>
        <td style={{ ...tdStyle, textAlign: "center" }}>
          <input
            type="checkbox"
            checked={!!entry.archived}
            onChange={onArchiveToggle}
            disabled={!isAdmin}
          />
        </td>
        <td style={{ ...tdStyle, textAlign: "right", whiteSpace: "nowrap" }}>
          {(Boolean(templateEnabled) || Editor !== undefined) && (
            /* 190 round 5 [P] (ruled 2026-09-16) — THE TOGGLE IS NOT RED.
               It wore `btn-danger`, the bin's family, and red in this app is
               code for deletion ("make the toggle to open them more visible
               (and not red …)"). MEASURED: four button families exist in
               App.css — `.btn-primary` (the accent tint, 80 uses: "+ Add",
               "Save", the affirmative act), `.btn-ghost` (outline, 204:
               export / import, the neutral act), `.btn-danger` (59: every
               bin), `.btn-back` (3) — and this row's other actions are the
               chevrons (bare) and the bin (danger). The OPEN/EDIT act takes
               the affirmative family, `btn-primary`, at the class's own size
               (the 1px/9px override that shrank it is gone; the icon 11 →
               13), pressed shade `--ag` (the family's own hover shade) while
               open, `aria-pressed` for the reader that cannot see it. */
            <button
              className="btn btn-primary"
              aria-pressed={showTpl}
              style={{
                marginRight: 4,
                background: showTpl ? "var(--ag)" : undefined,
              }}
              onClick={() => setShowTpl((s) => !s)}
              title={
                /* 189 §B — the same toggle opens a definition editor on the
                   kinds that carry one; the hover says which. */
                toggleTitle(t, Editor !== undefined, showTpl)
              }
            >
              <ListChecks size={13} />
            </button>
          )}
          {/* 181 B.2b [RULED 2026-09-11, Laurent, ruling 7] — THE BIN SAYS WHAT
              IT CAN DO. A refused row (the served `refusal`, B.2a — the same
              answer the dialog and the DELETE handler give) wears the bin the
              non-admin case already wears: disabled, dimmed, not-allowed, and
              the dialog's own sentence on hover through the SAME keys; a
              non-admin keeps "Admin only". The dialog and the DELETE handler
              stay the enforcing end. */}
          <button
            className="btn btn-danger"
            style={{
              padding: "1px 4px",
              fontSize: FS.s9,
              opacity: !isAdmin || refusal ? 0.4 : 1,
              cursor: !isAdmin || refusal ? "not-allowed" : "pointer",
            }}
            onClick={onDelete}
            disabled={!isAdmin || !!refusal}
            title={binTitle}
          >
            <Trash2 size={11} />
          </button>
        </td>
      </tr>
      {templateEnabled && showTpl && (
        <tr
          style={{
            borderBottom: "1px solid var(--br)",
            background: "var(--bg2)",
          }}
        >
          <td colSpan={colspan} style={{ padding: "8px 14px" }}>
            <SubtaskTemplateEditor
              subtasks={subtasks}
              isAdmin={isAdmin}
              onChange={onSubtasksChange}
            />
          </td>
        </tr>
      )}
      {Editor && showTpl && (
        <tr
          style={{
            borderBottom: "1px solid var(--br)",
            background: "var(--bg2)",
          }}
        >
          <td colSpan={Number(colspan)} style={{ padding: "8px 14px" }}>
            <Editor
              meta={(entry as { meta?: Record<string, unknown> | null }).meta ?? {}}
              isAdmin={Boolean(isAdmin)}
              onChange={(meta) => { metaChange({ id: entryId, meta }); }}
            />
          </td>
        </tr>
      )}
    </>
  );
}

/* 207C §5 — A TEXT FIELD OF AN ENTRY'S EDITOR: WHAT IS TYPED STAYS AS TYPED
   WHILE IT HAS FOCUS. The editors draw their fields from the NORMALISED meta
   (src/lib/crmDefinitions.mjs trims a column's label, an option's, a playbook
   task's title), so a space typed at the end of one vanished under the caret —
   « Kolom met » came out « Kolommet » once the lost keys were cured (measured,
   both engines). The text typed is still the one written, key by key, through
   the entry's queue (and stored as typed: the server does not trim); the field
   shows it until it is left, then the list's own face again. */
interface KeptTextProps {
  value: string;
  onText: Dispatch<string>;
  className?: string;
  style?: React.CSSProperties;
  placeholder?: string;
  disabled?: boolean;
}
function KeptText({ value, onText, className, style, placeholder, disabled }: Readonly<KeptTextProps>) {
  const [typed, setTyped] = useState<string | null>(null);
  return (
    <input
      className={className}
      style={style}
      placeholder={placeholder}
      disabled={disabled}
      value={typed ?? value}
      onFocus={() => {
        setTyped(value);
      }}
      onChange={(e) => {
        setTyped(e.target.value);
        onText(e.target.value);
      }}
      onBlur={() => {
        setTyped(null);
      }}
    />
  );
}

/* 189 §B — the hover sentence of the row's toggle: four sentences, one table
   keyed by what the toggle opens and whether it is open (a nested ternary is
   what one lint refuses; a boolean "selector" parameter is what another does). */
const TOGGLE_TITLE_KEYS = {
  "editor:open": ["taxonomies.editor.hide", "Hide definition"],
  "editor:closed": ["taxonomies.editor.edit", "Edit definition"],
  "template:open": ["taxonomies.templates.hide", "Hide subtask templates"],
  "template:closed": ["taxonomies.templates.edit", "Edit subtask templates"],
} as const;
function toggleTitle(t: TFn, editor: boolean, open: boolean): string {
  const [key, fallback] = TOGGLE_TITLE_KEYS[`${editor ? "editor" : "template"}:${open ? "open" : "closed"}`];
  return t(key, { fallback });
}

/* 189 §B — THE CUSTOM-FIELD DEFINITION EDITOR. Four things a label cannot say:
   which entities the field shows on, its type, whether it is a list column,
   and — for a list field — its options. The option's CODE is fixed when the
   option is added (a slug of its label, kept unique) so a renamed option keeps
   the values already stored under it; the label is the firm's data and is
   never a translation key. Typed end to end: a new editor takes no `any`. */
function CustomFieldDefEditor({ meta, isAdmin, onChange }: KindEditorProps) {
  const { t } = useTranslation() as { t: TFn };
  const def = normalizeCustomFieldMeta(meta);
  const [draftOption, setDraftOption] = useState("");
  const write = (patch: Partial<typeof def>) => { onChange({ ...meta, ...def, ...patch }); };
  const entityLabel: Record<string, string> = {
    client: t("taxonomies.customField.entity.client", { fallback: "Clients" }),
    case: t("taxonomies.customField.entity.case", { fallback: "Cases" }),
    cc: t("taxonomies.customField.entity.cc", { fallback: "Collective cases" }),
  };
  const typeLabel: Record<string, string> = {
    text: t("taxonomies.customField.type.text", { fallback: "Text" }),
    number: t("taxonomies.customField.type.number", { fallback: "Number" }),
    date: t("taxonomies.customField.type.date", { fallback: "Date" }),
    bool: t("taxonomies.customField.type.bool", { fallback: "Yes / no" }),
    select: t("taxonomies.customField.type.select", { fallback: "One of a list" }),
  };
  const toggleEntity = (e: string) => {
    const has = def.entities.includes(e);
    write({ entities: has ? def.entities.filter((x) => x !== e) : [...def.entities, e] });
  };
  const addOption = () => {
    const label = draftOption.trim();
    if (!label) return;
    let code = label.toLowerCase().replace(/[^a-z0-9]+/g, "_").split("_").filter(Boolean).join("_") || "option";
    const taken = new Set(def.options.map((o) => o.code));
    let n = 2;
    const base = code;
    while (taken.has(code)) code = `${base}_${String(n++)}`;
    write({ options: [...def.options, { code, label }] });
    setDraftOption("");
  };
  const patchOption = (i: number, label: string) => {
    write({ options: def.options.map((o, idx) => (idx === i ? { ...o, label } : o)) });
  };
  const removeOption = (i: number) => { write({ options: def.options.filter((_, idx) => idx !== i) }); };
  const row = { display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" as const, fontSize: FS.s11 };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{ fontSize: FS.s10, color: "var(--tm)" }}>
        {t("taxonomies.customField.help", {
          fallback:
            "A custom field appears on the forms of the entities ticked below, after the built-in fields, in this list's order. Its label is the row's label; the type decides how it is typed; a list field offers the options below.",
        })}
      </div>
      <div style={row}>
        <span style={{ fontWeight: 600 }}>{t("taxonomies.customField.entities", { fallback: "Shown on" })}</span>
        {CUSTOM_FIELD_ENTITIES.map((e: string) => (
          <label key={e} style={{ display: "inline-flex", alignItems: "center", gap: 4, cursor: isAdmin ? "pointer" : "default" }}>
            <input type="checkbox" checked={def.entities.includes(e)} disabled={!isAdmin} onChange={() => { toggleEntity(e); }} />
            {entityLabel[e]}
          </label>
        ))}
      </div>
      <div style={row}>
        <span style={{ fontWeight: 600 }}>{t("taxonomies.customField.type", { fallback: "Type" })}</span>
        <MenuSelect
          className="field-input"
          style={inputStyle(150)}
          value={def.type}
          onChange={(v) => { const type = asCustomFieldType(v); write({ type, options: type === "select" ? def.options : [] }); }}
          disabled={!isAdmin}
          options={CUSTOM_FIELD_TYPES.map((k: string) => [k, typeLabel[k]] as const)}
        />
        <label style={{ display: "inline-flex", alignItems: "center", gap: 4, cursor: isAdmin ? "pointer" : "default" }}>
          <input type="checkbox" checked={def.showInList} disabled={!isAdmin} onChange={(e) => { write({ showInList: e.target.checked }); }} />
          {t("taxonomies.customField.showInList", { fallback: "Show as a column in the list" })}
        </label>
      </div>
      {def.type === "select" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span style={{ fontWeight: 600, fontSize: FS.s11 }}>{t("taxonomies.customField.options", { fallback: "Options" })}</span>
          {def.options.length === 0 && (
            <span style={{ fontSize: FS.s11, color: "var(--tm)", fontStyle: "italic" }}>
              {t("taxonomies.customField.options_empty", { fallback: "No options yet — add one below." })}
            </span>
          )}
          {def.options.map((o, i) => (
            <div key={o.code} style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <code style={{ fontSize: FS.s10, color: "var(--tm)", minWidth: 90 }}>{o.code}</code>
              <KeptText
                className="field-input"
                style={{ ...inputStyle(), flex: 1, minWidth: 140 }}
                value={o.label}
                disabled={!isAdmin}
                onText={(v) => { patchOption(i, v); }}
              />
              <button type="button" className="btn btn-danger" style={btnStyle} disabled={!isAdmin} onClick={() => { removeOption(i); }}>
                <Trash2 size={11} />
              </button>
            </div>
          ))}
          {isAdmin && (
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <input
                className="field-input"
                style={{ ...inputStyle(), flex: 1, minWidth: 140 }}
                placeholder={t("taxonomies.customField.option_placeholder", { fallback: "option label" })}
                value={draftOption}
                onChange={(e) => { setDraftOption(e.target.value); }}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addOption(); } }}
              />
              <button type="button" className="btn btn-primary" style={btnStyle} onClick={addOption}>
                <Plus size={11} />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* 189 §C — THE STAGE-SET EDITOR: an ordered list of `case_stage` types, each
   with the optional party it is brought by (`by`, a `party_role` code — the
   two fields the stage modal itself asks for). Applying the set appends
   elements of exactly the shape `Case.stages` holds (crmDefinitions.mjs). */
function StageSetEditor({ meta, isAdmin, onChange }: KindEditorProps) {
  const { t } = useTranslation() as { t: TFn };
  const set = normalizeStageSetMeta(meta);
  const stageTypes = useTaxonomy("case_stage").entries as TaxList;
  const partyRoles = useTaxonomy("party_role").entries as TaxList;
  const [draftType, setDraftType] = useState("");
  const write = (stages: { type: string; by: string }[]) => { onChange({ ...meta, stages }); };
  const codesOf = (entries: TaxList): readonly [string, string][] =>
    (entries ?? []).flatMap((e) => (e && !Array.isArray(e) && typeof e.code === "string" ? [[e.code, resolveTaxonomyLabel(e.code, entries)] as const] : []));
  const typeOptions = codesOf(stageTypes);
  const byOptions = codesOf(partyRoles);
  const add = () => {
    if (!draftType) return;
    write([...set.stages, { type: draftType, by: "" }]);
    setDraftType("");
  };
  const patch = (i: number, p: Partial<{ type: string; by: string }>) => { write(set.stages.map((s, idx) => (idx === i ? { ...s, ...p } : s))); };
  const remove = (i: number) => { write(set.stages.filter((_, idx) => idx !== i)); };
  const move = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= set.stages.length) return;
    const next = set.stages.slice();
    [next[i], next[j]] = [next[j], next[i]];
    write(next);
  };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <div style={{ fontSize: FS.s10, color: "var(--tm)" }}>
        {t("taxonomies.stageSet.help", { fallback: "Applying this set to a case appends these stages, in this order, dated the day it is applied. The case's existing stages are never touched." })}
      </div>
      {set.stages.length === 0 && (
        <span style={{ fontSize: FS.s11, color: "var(--tm)", fontStyle: "italic" }}>
          {t("taxonomies.stageSet.empty", { fallback: "No stages yet — add one below." })}
        </span>
      )}
      {set.stages.map((s, i) => (
        <div key={`${String(i)}-${s.type}`} style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <span style={{ width: 18, textAlign: "right", fontSize: FS.s10, color: "var(--tm)" }}>{i + 1}</span>
          <MenuSelect className="field-input" style={inputStyle(200)} value={s.type} disabled={!isAdmin} options={typeOptions} onChange={(v) => { patch(i, { type: v }); }} />
          <MenuSelect className="field-input" style={inputStyle(160)} value={s.by} disabled={!isAdmin} options={[["", t("common.none_dashed", { fallback: "— None —" })] as const, ...byOptions]} onChange={(v) => { patch(i, { by: v }); }} />
          <button type="button" style={chevStyle(!isAdmin || i === 0)} disabled={!isAdmin || i === 0} onClick={() => { move(i, -1); }}><ChevronUp size={12} /></button>
          <button type="button" style={chevStyle(!isAdmin || i === set.stages.length - 1)} disabled={!isAdmin || i === set.stages.length - 1} onClick={() => { move(i, 1); }}><ChevronDown size={12} /></button>
          <button type="button" className="btn btn-danger" style={btnStyle} disabled={!isAdmin} onClick={() => { remove(i); }}><Trash2 size={11} /></button>
        </div>
      ))}
      {isAdmin && (
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <MenuSelect className="field-input" style={inputStyle(200)} value={draftType} options={[["", t("common.select_dots", { fallback: "Select..." })] as const, ...typeOptions]} onChange={setDraftType} />
          <button type="button" className="btn btn-primary" style={btnStyle} disabled={!draftType} onClick={add}><Plus size={11} /></button>
        </div>
      )}
    </div>
  );
}

/* 189 §H — THE SEGMENT EDITOR: what the app knows about a client, as a
   query — type, language, archived, prospects, and the firm's own fields
   (§B) as equalities. Saved on the row's meta.filter; the newsletter resolves
   it at fire time (server/src/lib/mailSegments.js) — never frozen. */
function SegmentEditor({ meta, isAdmin, onChange }: KindEditorProps) {
  const { t, lang } = useTranslation() as { t: TFn; lang: string };
  const f = normalizeSegmentFilter(meta);
  const clientTypes = useTaxonomy("client_type").entries as TaxList;
  const defs = customFieldDefsFor(useTaxonomy("custom_field").entries, "client");
  const write = (patch: Partial<typeof f>) => { onChange({ ...meta, filter: { ...f, ...patch } }); };
  const codesOf = (entries: TaxList): readonly [string, string][] =>
    (entries ?? []).flatMap((e) => (e && !Array.isArray(e) && typeof e.code === "string" ? [[e.code, resolveTaxonomyLabel(e.code, entries)] as const] : []));
  const toggle = (list: string[], v: string) => (list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);
  const LANGS: readonly [string, string][] = [
    ["fr", t("lang.french", { fallback: "French" })],
    ["nl", t("lang.dutch", { fallback: "Dutch" })],
    ["en", t("lang.english", { fallback: "English" })],
    ["de", t("lang.german", { fallback: "German" })],
  ];
  const TRI: readonly [string, string][] = [
    ["exclude", t("taxonomies.segment.tri.exclude", { fallback: "Excluded" })],
    ["only", t("taxonomies.segment.tri.only", { fallback: "Only these" })],
    ["any", t("taxonomies.segment.tri.any", { fallback: "Included" })],
  ];
  const cfValue = (code: string) => f.customFields.find((c) => c.code === code);
  const setCf = (code: string, raw: string) => {
    const rest = f.customFields.filter((c) => c.code !== code);
    write({ customFields: raw === "" ? rest : [...rest, { code, value: raw }] });
  };
  const row = { display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" as const, fontSize: FS.s11 };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{ fontSize: FS.s10, color: "var(--tm)" }}>
        {t("taxonomies.segment.help", { fallback: "A newsletter over this segment goes to every client matching ALL the conditions below, resolved when it is fired — never a frozen list. The consent filter applies on top." })}
      </div>
      <div style={row}>
        <span style={{ fontWeight: 600 }}>{t("taxonomies.segment.client_types", { fallback: "Client types" })}</span>
        {codesOf(clientTypes).map(([code, label]) => (
          <label key={code} style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
            <input type="checkbox" checked={f.clientTypes.includes(code)} disabled={!isAdmin} onChange={() => { write({ clientTypes: toggle(f.clientTypes, code) }); }} />
            {label}
          </label>
        ))}
        <span style={{ color: "var(--tm)" }}>{t("taxonomies.segment.none_means_any", { fallback: "(none ticked = any)" })}</span>
      </div>
      <div style={row}>
        <span style={{ fontWeight: 600 }}>{t("taxonomies.segment.languages", { fallback: "Languages" })}</span>
        {LANGS.map(([code, label]) => (
          <label key={code} style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
            <input type="checkbox" checked={f.languages.includes(code)} disabled={!isAdmin} onChange={() => { write({ languages: toggle(f.languages, code) }); }} />
            {label}
          </label>
        ))}
      </div>
      <div style={row}>
        <span style={{ fontWeight: 600 }}>{t("taxonomies.segment.archived", { fallback: "Archived clients" })}</span>
        <MenuSelect className="field-input" style={inputStyle(140)} value={f.archived} disabled={!isAdmin} options={TRI} onChange={(v) => { write({ archived: v === "only" || v === "any" ? v : "exclude" }); }} />
        <span style={{ fontWeight: 600 }}>{t("taxonomies.segment.prospects", { fallback: "Prospects" })}</span>
        <MenuSelect className="field-input" style={inputStyle(140)} value={f.prospects} disabled={!isAdmin} options={TRI} onChange={(v) => { write({ prospects: v === "only" || v === "exclude" ? v : "any" }); }} />
      </div>
      {defs.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span style={{ fontWeight: 600, fontSize: FS.s11 }}>{t("taxonomies.segment.custom_fields", { fallback: "Custom fields (equal to)" })}</span>
          {defs.map((d) => {
            const code = String(d.code);
            const m = normalizeCustomFieldMeta(d.meta);
            const cur = cfValue(code);
            const val = cur ? String(cur.value) : "";
            // A list field offers its options, a yes/no its two answers, the
            // rest a text box — one option list per type, no nested ternary.
            const any: readonly [string, string] = ["", t("common.any", { fallback: "Any" })];
            const byType: Partial<Record<string, readonly (readonly [string, string])[]>> = {
              select: [any, ...m.options.map((o) => [o.code, o.label] as const)],
              bool: [any, ["true", t("common.yes", { fallback: "Yes" })], ["false", t("common.no", { fallback: "No" })]],
            };
            const listOptions = byType[m.type];
            return (
              <div key={code} style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <span style={{ minWidth: 160, fontSize: FS.s11 }}>{customFieldLabel(d, lang)}</span>
                {listOptions ? (
                  <MenuSelect className="field-input" style={inputStyle(180)} value={val} disabled={!isAdmin} options={listOptions} onChange={(v) => { setCf(code, v); }} />
                ) : (
                  <input className="field-input" style={inputStyle(180)} value={val} disabled={!isAdmin} placeholder={any[1]} onChange={(e) => { setCf(code, e.target.value); }} />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* 189 §D — THE PLAYBOOK EDITOR: one of the two coded triggers (no rule engine,
   ever), the stage type when the trigger is a stage joining the case, and the
   task templates — title, who it goes to (a rule resolved on the case at fire
   time), the due date as days from firing, an optional activity type. The
   tasks it creates are ordinary tasks (lib/playbooks.js). */
/* 190 §C — THE BENTO TEMPLATE EDITOR: the template's COLUMNS (an ordered
   list, code + label — the stage-set editor's rows) and the SURFACES it may
   be offered on (Laurent's (b)): the three Bentos are NOT interchangeable —
   `user` and `case` are over TASKS, `list` is over CASES — so a template says
   which may offer it, and the editor says so in words. A placement names a
   column by its code (BentoPlacement.column); renaming a label keeps the
   code, so nothing placed moves. The meta shape is crmDefinitions.mjs's. */
/* 196 §A [RULED, Laurent 2026-09-19] — A COLUMN IS NAMED IN THE THREE
   LANGUAGES, AS THE MODEL'S OWN NAME IS: `label` (en) · `labelFr` · `labelNl`,
   the model row's three fields and their three placeholders, per column and
   on the add row; `label` alone is required (the code is slugged from it), so
   a column saved before 196 — one field, whatever language he was thinking
   in — edits and reads exactly as it did. The shape and the fallback are the
   normaliser's (crmDefinitions.mjs `BentoColumn` / `bentoColumnLabel`). */
type BentoColumnLabelKey = "label" | "labelFr" | "labelNl";
/** 201 §B.1 — the three places, as the normaliser spells them (`BENTO_SURFACES`). */
type BentoPlace = "user" | "case" | "list";
const BENTO_COLUMN_LABEL_FIELDS: readonly { key: BentoColumnLabelKey; pk: string; ph: string }[] = [
  { key: "label", pk: "taxonomies.label_en_placeholder", ph: "label (EN)" },
  { key: "labelFr", pk: "taxonomies.label_fr_placeholder", ph: "libellé (FR)" },
  { key: "labelNl", pk: "taxonomies.label_nl_placeholder", ph: "label (NL)" },
];
function BentoTemplateEditor({ meta, isAdmin, onChange }: KindEditorProps) {
  const { t, lang } = useTranslation() as { t: TFn; lang: string };
  const tpl = normalizeBentoTemplateMeta(meta);
  const [draft, setDraft] = useState<Record<BentoColumnLabelKey, string>>({ label: "", labelFr: "", labelNl: "" });
  const write = (patch: Partial<typeof tpl>) => { onChange({ ...meta, ...tpl, ...patch }); };
  /* 190 glance round 2 [C], RULED — the LIST card's fields, per template: the
     case list's own columns (BENTO_CASE_FIELDS, the header keys already
     seeded) and the custom fields a case or a collective case shows in lists
     (`cf_<code>`, the columns' own keys). Offered only while the template is
     offered on the list surface — the two task Bentos render the matrix's
     card and have no field to choose. */
  const cfDefs = useTaxonomy("custom_field").entries as TaxList;
  const cfCandidates = [...customFieldDefsFor(cfDefs, "case"), ...customFieldDefsFor(cfDefs, "cc")]
    .filter((d, i, arr) => arr.findIndex((x) => x.code === d.code) === i)
    .map((d) => ({ k: `cf_${String(d.code)}`, label: customFieldLabel(d, lang) }));
  const fieldCandidates: readonly { k: string; label: string }[] = [
    ...BENTO_CASE_FIELDS.map((f) => ({ k: f.k, label: t(f.lk, { fallback: f.l }) })),
    ...cfCandidates,
  ];
  const toggleField = (k: string) => {
    write({ fields: tpl.fields.includes(k) ? tpl.fields.filter((x) => x !== k) : [...tpl.fields, k] });
  };
  const surfaceLabel: Record<string, string> = {
    user: t("taxonomies.bento.surface.user", { fallback: "My Bento (my own tasks, beside my Eisenhower)" }),
    case: t("taxonomies.bento.surface.case", { fallback: "A case's Bento (that case's tasks, beside its matrix)" }),
    list: t("taxonomies.bento.surface.list", { fallback: "The list Bento (cases and collective cases, over the lists)" }),
  };
  /* 201 §B.1 — unticking a place also drops this model's default there in
     the SAME write (the normaliser would read it as false anyway; the row
     says what it means). */
  const toggleSurface = (k: string) => {
    const on = !tpl.surfaces[k as BentoPlace];
    write({ surfaces: { ...tpl.surfaces, [k]: on }, ...(on ? {} : { defaults: { ...tpl.defaults, [k]: false } }) });
  };
  /* 201 §B.1 [RULED — Laurent, 2026-09-21] — THE DEFAULT PER PLACE, AND
     « IMPOSE EVERYWHERE », IN THE MODEL'S OWN META. One radio per place the
     model IS offered on (a model not offered on a place cannot be its default:
     the radio is absent, not disabled-and-ticked); ticking it names this model
     the firm's default there — what a colleague who has picked nothing sees,
     and what a new colleague starts on. ONLY ONE MODEL IS DEFAULT PER PLACE,
     and that is a rule across rows a PATCH of one row cannot keep: the SERVER
     keeps it (routes/taxonomies.js, the PATCH clears that place on every other
     model in the same transaction), and `reloadAll()` after the write brings
     the loser's row back unticked. A radio cannot be unticked by a click, so a
     click on the ticked one CLEARS it (the hover says so) — a place may have no
     default, as it had none before 201. Then ONE toggle, « Impose everywhere »,
     shown once the model is the default of at least one place (the
     `allMatters` precedent above: a flag that appears only when its ground is
     ticked): while on, everyone sees this model on that place, personal picks
     ignored and never erased (useBento reads it; the picker refuses with the
     toggle's own sentence). */
  const offeredPlaces = BENTO_SURFACES.filter((k) => tpl.surfaces[k]);
  const setDefault = (k: BentoPlace, on: boolean) => { write({ defaults: { ...tpl.defaults, [k]: on } }); };
  const isDefaultSomewhere = BENTO_SURFACES.some((k) => tpl.defaults[k]);
  const imposeTitle = t("taxonomies.bento.impose_title", { fallback: "While this is on, everyone sees this template on that place; personal choices are ignored, not erased, and turning it off gives everyone their own back." });
  const addColumn = () => {
    if (!draft.label.trim()) return;
    write({ columns: normalizeBentoTemplateMeta({ columns: [...tpl.columns, draft] }).columns });
    setDraft({ label: "", labelFr: "", labelNl: "" });
  };
  const patchColumn = (i: number, key: BentoColumnLabelKey, value: string) => { write({ columns: tpl.columns.map((c, idx) => (idx === i ? { ...c, [key]: value } : c)) }); };
  const removeColumn = (i: number) => { write({ columns: tpl.columns.filter((_, idx) => idx !== i) }); };
  const moveColumn = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= tpl.columns.length) return;
    const next = tpl.columns.slice();
    [next[i], next[j]] = [next[j], next[i]];
    write({ columns: next });
  };
  const row: React.CSSProperties = { display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: FS.s11 }}>
      <div style={{ fontSize: FS.s10, color: "var(--tm)" }}>
        {t("taxonomies.bento.help", {
          fallback:
            "A Bento is a view, never a record: a card moved to a column writes where it was put and nothing else. The first column holds every card nobody has placed. A template is offered only on the Bentos ticked below — the first two are over tasks, the third over cases, and they are not interchangeable.",
        })}
      </div>
      <div style={row}>
        <span style={{ fontWeight: 600 }}>{t("taxonomies.bento.offered_on", { fallback: "This Bento template is offered on" })}</span>
        {BENTO_SURFACES.map((k: string) => (
          <label key={k} style={{ display: "inline-flex", alignItems: "center", gap: 4, cursor: isAdmin ? "pointer" : "default" }}>
            <input type="checkbox" checked={tpl.surfaces[k as keyof typeof tpl.surfaces]} disabled={!isAdmin} onChange={() => { toggleSurface(k); }} />
            {surfaceLabel[k]}
          </label>
        ))}
      </div>
      {offeredPlaces.length > 0 && (
        <div style={row}>
          <span style={{ fontWeight: 600 }} title={t("taxonomies.bento.default_here_title", { fallback: "The firm's default on that place: what a colleague who has picked nothing sees, and where a new colleague starts. One template per place — ticking this one clears the other; a click on the ticked one clears it." })}>
            {t("taxonomies.bento.default_here", { fallback: "Default here" })}
          </span>
          {offeredPlaces.map((k) => (
            <label key={k} style={{ display: "inline-flex", alignItems: "center", gap: 4, cursor: isAdmin ? "pointer" : "default" }} title={t("taxonomies.bento.default_here_title", { fallback: "The firm's default on that place: what a colleague who has picked nothing sees, and where a new colleague starts. One template per place — ticking this one clears the other; a click on the ticked one clears it." })}>
              <input
                type="radio"
                name={`bento-default-${k}`}
                checked={tpl.defaults[k]}
                disabled={!isAdmin}
                onChange={() => { setDefault(k, true); }}
                onClick={() => { if (tpl.defaults[k]) setDefault(k, false); }}
              />
              {surfaceLabel[k]}
            </label>
          ))}
        </div>
      )}
      {isDefaultSomewhere && (
        <label style={{ display: "inline-flex", alignItems: "center", gap: 6, cursor: isAdmin ? "pointer" : "default", fontSize: FS.s11 }} title={imposeTitle}>
          <input type="checkbox" checked={tpl.impose} disabled={!isAdmin} onChange={() => { write({ impose: !tpl.impose }); }} title={imposeTitle} />
          {t("taxonomies.bento.impose", { fallback: "Impose everywhere" })}
        </label>
      )}
      {tpl.surfaces.list && (
        /* 190 round 6 [S](i) — ONE flag beside the surfaces: this Bento
           template shows cases AND collective cases merged on either list
           (the picker then says " - TOUS"). */
        <label style={{ display: "inline-flex", alignItems: "center", gap: 6, cursor: isAdmin ? "pointer" : "default", fontSize: FS.s11 }}>
          <input type="checkbox" checked={tpl.allMatters} disabled={!isAdmin} onChange={() => { write({ allMatters: !tpl.allMatters }); }} />
          {t("taxonomies.bento.all_matters", { fallback: "This Bento template shows cases and collective cases together, on either list" })}
        </label>
      )}
      {tpl.surfaces.list && (
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span style={{ fontWeight: 600, fontSize: FS.s11 }}>{t("taxonomies.bento.card_fields", { fallback: "Fields on a case card of the list Bento, under its reference and title" })}</span>
          <span style={{ fontSize: FS.s10, color: "var(--tm)" }}>
            {t("taxonomies.bento.card_fields_help", { fallback: "Tick what this Bento template's cards show — a template about finances need not show what a template about procedure shows. Each field reads as it does in the case list; the amount due is the case's own Dû." })}
          </span>
          <div style={{ ...row, gap: 8 }}>
            {fieldCandidates.map((f) => (
              <label key={f.k} style={{ display: "inline-flex", alignItems: "center", gap: 4, cursor: isAdmin ? "pointer" : "default" }}>
                <input type="checkbox" checked={tpl.fields.includes(f.k)} disabled={!isAdmin} onChange={() => { toggleField(f.k); }} />
                {f.label}
              </label>
            ))}
          </div>
        </div>
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <span style={{ fontWeight: 600, fontSize: FS.s11 }}>{t("taxonomies.bento.columns_ordered", { fallback: "Bento columns, in order (the first holds the unplaced)" })}</span>
        {tpl.columns.length === 0 && (
          <span style={{ fontSize: FS.s11, color: "var(--tm)", fontStyle: "italic" }}>
            {t("taxonomies.bento.no_columns_yet", { fallback: "No Bento columns yet — add one below." })}
          </span>
        )}
        {tpl.columns.map((c, i) => (
          <div key={c.code} style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <span style={{ width: 18, textAlign: "right", fontSize: FS.s10, color: "var(--tm)" }}>{i + 1}</span>
            <code style={{ fontSize: FS.s10, color: "var(--tm)", minWidth: 90 }}>{c.code}</code>
            {BENTO_COLUMN_LABEL_FIELDS.map((f) => (
              <KeptText
                key={f.key}
                className="field-input"
                style={{ ...inputStyle(), flex: 1, minWidth: 140 }}
                placeholder={t(f.pk, { fallback: f.ph })}
                value={c[f.key] ?? ""}
                disabled={!isAdmin}
                onText={(v) => { patchColumn(i, f.key, v); }}
              />
            ))}
            <button type="button" style={chevStyle(!isAdmin || i === 0)} disabled={!isAdmin || i === 0} onClick={() => { moveColumn(i, -1); }}><ChevronUp size={12} /></button>
            <button type="button" style={chevStyle(!isAdmin || i === tpl.columns.length - 1)} disabled={!isAdmin || i === tpl.columns.length - 1} onClick={() => { moveColumn(i, 1); }}><ChevronDown size={12} /></button>
            <button type="button" className="btn btn-danger" style={btnStyle} disabled={!isAdmin} onClick={() => { removeColumn(i); }}><Trash2 size={11} /></button>
          </div>
        ))}
        {isAdmin && (
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            {BENTO_COLUMN_LABEL_FIELDS.map((f) => (
              <input
                key={f.key}
                className="field-input"
                style={{ ...inputStyle(), flex: 1, minWidth: 140 }}
                placeholder={t(f.pk, { fallback: f.ph })}
                value={draft[f.key]}
                onChange={(e) => { const v = e.target.value; setDraft((d) => ({ ...d, [f.key]: v })); }}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addColumn(); } }}
              />
            ))}
            <button type="button" className="btn btn-primary" style={btnStyle} disabled={!draft.label.trim()} onClick={addColumn}><Plus size={11} /></button>
          </div>
        )}
      </div>
    </div>
  );
}

function PlaybookEditor({ meta, isAdmin, onChange }: KindEditorProps) {
  const { t } = useTranslation() as { t: TFn };
  const pb = normalizePlaybookMeta(meta, { keepUntitled: true }); // 208 §13 — an emptied title stays, empty, until removed
  const stageTypes = useTaxonomy("case_stage").entries as TaxList;
  const activityTypes = useTaxonomy("activity_code").entries as TaxList;
  const [draftTitle, setDraftTitle] = useState("");
  const write = (patch: Partial<typeof pb>) => { onChange({ ...meta, ...pb, ...patch }); };
  const codesOf = (entries: TaxList): readonly [string, string][] =>
    (entries ?? []).flatMap((e) => (e && !Array.isArray(e) && typeof e.code === "string" ? [[e.code, resolveTaxonomyLabel(e.code, entries)] as const] : []));
  const triggerLabel: Record<string, string> = {
    case_created: t("taxonomies.playbook.trigger.case_created", { fallback: "A case is created (or a prospect converts)" }),
    stage_added: t("taxonomies.playbook.trigger.stage_added", { fallback: "A stage is added to a case" }),
  };
  const assigneeLabel: Record<string, string> = {
    responsible_partner: t("taxonomies.playbook.assignee.responsible_partner", { fallback: "Responsible partners" }),
    case_team: t("taxonomies.playbook.assignee.case_team", { fallback: "The case team" }),
    creator: t("taxonomies.playbook.assignee.creator", { fallback: "Whoever triggered it" }),
    nobody: t("taxonomies.playbook.assignee.nobody", { fallback: "Nobody" }),
  };
  const add = () => {
    const title = draftTitle.trim();
    if (!title) return;
    write({ tasks: [...pb.tasks, { title, assignee: "responsible_partner", dueDays: 7, type: null }] });
    setDraftTitle("");
  };
  const patch = (i: number, p: Partial<(typeof pb.tasks)[number]>) => { write({ tasks: pb.tasks.map((x, idx) => (idx === i ? { ...x, ...p } : x)) }); };
  const remove = (i: number) => { write({ tasks: pb.tasks.filter((_, idx) => idx !== i) }); };
  const move = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= pb.tasks.length) return;
    const next = pb.tasks.slice();
    [next[i], next[j]] = [next[j], next[i]];
    write({ tasks: next });
  };
  const row = { display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" as const, fontSize: FS.s11 };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{ fontSize: FS.s10, color: "var(--tm)" }}>
        {t("taxonomies.playbook.help", { fallback: "When the trigger fires on a case, these tasks are created on it, once — firing the same trigger twice never duplicates them. Due = days after the firing day." })}
      </div>
      <div style={row}>
        <span style={{ fontWeight: 600 }}>{t("taxonomies.playbook.trigger", { fallback: "Trigger" })}</span>
        <MenuSelect
          className="field-input"
          style={inputStyle(300)}
          value={pb.trigger}
          disabled={!isAdmin}
          options={PLAYBOOK_TRIGGERS.map((k: string) => [k, triggerLabel[k]] as const)}
          onChange={(v) => { write({ trigger: v, stageType: v === "stage_added" ? pb.stageType : "" }); }}
        />
        {pb.trigger === "stage_added" && (
          <MenuSelect
            className="field-input"
            style={inputStyle(200)}
            value={pb.stageType}
            disabled={!isAdmin}
            options={[["", t("common.select_dots", { fallback: "Select..." })] as const, ...codesOf(stageTypes)]}
            onChange={(v) => { write({ stageType: v }); }}
          />
        )}
      </div>
      <span style={{ fontWeight: 600, fontSize: FS.s11 }}>{t("taxonomies.playbook.tasks", { fallback: "Tasks to create" })}</span>
      {pb.tasks.length === 0 && (
        <span style={{ fontSize: FS.s11, color: "var(--tm)", fontStyle: "italic" }}>
          {t("taxonomies.playbook.tasks_empty", { fallback: "No task yet — add one below." })}
        </span>
      )}
      {/* 208 §13 — a row keeps ONE identity while its title is typed, its place (as the subtask templates' rows below):
          a key built from the title remounted the row on every keystroke — the caret left it after one key, and an
          emptied title (which now stays) could never be typed back. */}
      {pb.tasks.map((task, i) => (
        <div key={i} style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
          <span style={{ width: 18, textAlign: "right", fontSize: FS.s10, color: "var(--tm)" }}>{i + 1}</span>
          <KeptText className="field-input" style={{ ...inputStyle(), flex: 1, minWidth: 180 }} value={task.title} disabled={!isAdmin} onText={(v) => { patch(i, { title: v }); }} />
          <MenuSelect className="field-input" style={inputStyle(170)} value={task.assignee} disabled={!isAdmin} options={PLAYBOOK_ASSIGNEES.map((k: string) => [k, assigneeLabel[k]] as const)} onChange={(v) => { patch(i, { assignee: v }); }} />
          <label style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: FS.s10, color: "var(--tm)" }}>
            {t("taxonomies.playbook.due_days", { fallback: "due in" })}
            <input className="field-input" type="number" style={inputStyle(60)} value={task.dueDays} disabled={!isAdmin} onChange={(e) => { patch(i, { dueDays: Math.trunc(Number(e.target.value) || 0) }); }} />
            {t("taxonomies.playbook.days", { fallback: "days" })}
          </label>
          <MenuSelect className="field-input" style={inputStyle(150)} value={task.type ?? ""} disabled={!isAdmin} options={[["", t("common.none_dashed", { fallback: "— None —" })] as const, ...codesOf(activityTypes)]} onChange={(v) => { patch(i, { type: v || null }); }} />
          <button type="button" style={chevStyle(!isAdmin || i === 0)} disabled={!isAdmin || i === 0} onClick={() => { move(i, -1); }}><ChevronUp size={12} /></button>
          <button type="button" style={chevStyle(!isAdmin || i === pb.tasks.length - 1)} disabled={!isAdmin || i === pb.tasks.length - 1} onClick={() => { move(i, 1); }}><ChevronDown size={12} /></button>
          <button type="button" className="btn btn-danger" style={btnStyle} disabled={!isAdmin} onClick={() => { remove(i); }}><Trash2 size={11} /></button>
        </div>
      ))}
      {isAdmin && (
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <input
            className="field-input"
            style={{ ...inputStyle(), flex: 1, minWidth: 180 }}
            placeholder={t("taxonomies.playbook.task_placeholder", { fallback: "task title" })}
            value={draftTitle}
            onChange={(e) => { setDraftTitle(e.target.value); }}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          />
          <button type="button" className="btn btn-primary" style={btnStyle} disabled={!draftTitle.trim()} onClick={add}><Plus size={11} /></button>
        </div>
      )}
    </div>
  );
}

// Subtask template editor: a list of {labelEn, labelFr, labelNl, offsetDays}
// pairs, ordered. Negative offsets mean "before parent due date" (e.g.
// -25 = first draft 25 days before deadline). Phase 7 — three controlled
// label inputs replace the single legacy `label` field; offsetDays kept
// in a fourth input. Manual reorder via arrows (no auto-sort).
function SubtaskTemplateEditor({ subtasks, isAdmin, onChange }: any) {
  const { t } = useTranslation();
  const [draftLabelEn, setDraftLabelEn] = useState("");
  const [draftLabelFr, setDraftLabelFr] = useState("");
  const [draftLabelNl, setDraftLabelNl] = useState("");
  const [draftOffset, setDraftOffset] = useState("-7");
  function add() {
    const labelEn = draftLabelEn.trim();
    if (!labelEn) return;
    const offsetDays = Number(draftOffset);
    if (!Number.isFinite(offsetDays)) return;
    onChange([
      ...subtasks,
      {
        labelEn,
        labelFr: draftLabelFr.trim(),
        labelNl: draftLabelNl.trim(),
        offsetDays,
      },
    ]);
    setDraftLabelEn("");
    setDraftLabelFr("");
    setDraftLabelNl("");
    setDraftOffset("-7");
  }
  function patch(i: any, key: any, value: any) {
    const next = subtasks.map((s: any, idx: any) =>
      idx === i ? { ...s, [key]: value } : s,
    );
    onChange(next);
  }
  function remove(i: any) {
    onChange(subtasks.filter((_: any, idx: any) => idx !== i));
  }
  function move(i: any, dir: any) {
    const j = dir === "up" ? i - 1 : i + 1;
    if (j < 0 || j >= subtasks.length) return;
    const next = subtasks.slice();
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <div style={{ fontSize: FS.s10, color: "var(--tm)", marginBottom: 2 }}>
        {t("taxonomies.template.help", {
          fallback:
            "Subtask templates auto-inject on creation. Offset = days from parent due date (e.g. -25 = 25 days before).",
        })}
      </div>
      {subtasks.length === 0 && (
        <div
          style={{
            fontSize: FS.s11,
            color: "var(--tm)",
            fontStyle: "italic",
            padding: "4px 0",
          }}
        >
          {t("taxonomies.template.empty", {
            fallback: "No templates yet. Add one below.",
          })}
        </div>
      )}
      {subtasks.length > 0 && (
        <div
          style={{
            display: "flex",
            gap: 6,
            alignItems: "center",
            fontSize: FS.s9,
            color: "var(--tm)",
            textTransform: "uppercase",
            letterSpacing: ".4px",
            fontWeight: 600,
          }}
        >
          <span style={{ flex: 1, minWidth: 120, padding: "0 4px" }}>EN</span>
          <span style={{ flex: 1, minWidth: 120, padding: "0 4px" }}>FR</span>
          <span style={{ flex: 1, minWidth: 120, padding: "0 4px" }}>NL</span>
          <span style={{ width: 70, padding: "0 4px" }}>
            {t("taxonomies.template.offset_label", { fallback: "offset:" })}
          </span>
          <span style={{ width: 90 }} />
        </div>
      )}
      {subtasks.map((s: any, i: any) => {
        const labelEn = s.labelEn ?? s.label ?? "";
        const labelFr = s.labelFr ?? "";
        const labelNl = s.labelNl ?? "";
        return (
          <div
            key={i}
            style={{ display: "flex", gap: 6, alignItems: "center" }}
          >
            <input
              className="field-input"
              style={{ ...inputStyle(), flex: 1, minWidth: 120 }}
              value={labelEn}
              onChange={(e) => patch(i, "labelEn", e.target.value)}
              disabled={!isAdmin}
              placeholder="EN"
            />
            <input
              className="field-input"
              style={{ ...inputStyle(), flex: 1, minWidth: 120 }}
              value={labelFr}
              onChange={(e) => patch(i, "labelFr", e.target.value)}
              disabled={!isAdmin}
              placeholder="FR"
            />
            <input
              className="field-input"
              style={{ ...inputStyle(), flex: 1, minWidth: 120 }}
              value={labelNl}
              onChange={(e) => patch(i, "labelNl", e.target.value)}
              disabled={!isAdmin}
              placeholder="NL"
            />
            <input
              className="field-input"
              style={inputStyle(70)}
              type="number"
              value={s.offsetDays ?? 0}
              onChange={(e) => {
                const v = Number(e.target.value);
                if (Number.isFinite(v)) patch(i, "offsetDays", v);
              }}
              disabled={!isAdmin}
            />
            <button
              style={chevStyle(i === 0 || !isAdmin)}
              onClick={() => move(i, "up")}
              disabled={i === 0 || !isAdmin}
            >
              <ChevronUp size={12} />
            </button>
            <button
              style={chevStyle(i === subtasks.length - 1 || !isAdmin)}
              onClick={() => move(i, "down")}
              disabled={i === subtasks.length - 1 || !isAdmin}
            >
              <ChevronDown size={12} />
            </button>
            <button
              className="btn btn-danger"
              style={{ padding: "1px 4px", fontSize: FS.s9 }}
              onClick={() => remove(i)}
              disabled={!isAdmin}
            >
              <Trash2 size={11} />
            </button>
          </div>
        );
      })}
      <div
        style={{
          display: "flex",
          gap: 6,
          alignItems: "center",
          marginTop: 4,
          paddingTop: 6,
          borderTop: "1px dashed var(--br)",
        }}
      >
        <input
          className="field-input"
          style={{ ...inputStyle(), flex: 1, minWidth: 120 }}
          placeholder={t("taxonomies.template.label_en_placeholder", {
            fallback: "EN label",
          })}
          value={draftLabelEn}
          onChange={(e) => setDraftLabelEn(e.target.value)}
          disabled={!isAdmin}
        />
        <input
          className="field-input"
          style={{ ...inputStyle(), flex: 1, minWidth: 120 }}
          placeholder={t("taxonomies.template.label_fr_placeholder", {
            fallback: "FR label",
          })}
          value={draftLabelFr}
          onChange={(e) => setDraftLabelFr(e.target.value)}
          disabled={!isAdmin}
        />
        <input
          className="field-input"
          style={{ ...inputStyle(), flex: 1, minWidth: 120 }}
          placeholder={t("taxonomies.template.label_nl_placeholder", {
            fallback: "NL label",
          })}
          value={draftLabelNl}
          onChange={(e) => setDraftLabelNl(e.target.value)}
          disabled={!isAdmin}
        />
        <input
          className="field-input"
          style={inputStyle(70)}
          type="number"
          value={draftOffset}
          onChange={(e) => setDraftOffset(e.target.value)}
          disabled={!isAdmin}
        />
        <button
          className="btn btn-primary"
          style={{
            padding: "2px 8px",
            fontSize: FS.s10,
            display: "flex",
            alignItems: "center",
            gap: 3,
          }}
          onClick={add}
          disabled={!isAdmin || !draftLabelEn.trim()}
        >
          <Plus size={12} /> {t("common.add", { fallback: "Add" })}
        </button>
      </div>
    </div>
  );
}

// 200 §E — a row's meta read ONCE into a typed record (the ratchet's rule for
// a field the editor reads off an `any` row).
function metaRecord(m: unknown): Record<string, unknown> {
  return m && typeof m === "object" ? (m as Record<string, unknown>) : {};
}

function isColorField(f: any) {
  return typeof f === "string" && f.toLowerCase().includes("color");
}

// Column header for a meta field. Most keys read fine raw (color, icon, url);
// `eisen` and `firmWide` do not, so they get human headers.
function metaFieldHeader(f: any, t: Translate) {
  if (f === "eisen")
    return t("taxonomies.col.eisen", { fallback: "Default quadrant" });
  if (f === "firmWide")
    return t("taxonomies.col.firmWide", { fallback: "Whole firm sees it" });
  if (f === "m365ImportRole")
    return t("taxonomies.col.m365ImportRole", { fallback: "M365 import" });
  /* Brief 57 §1. No `fallback:`, same rule as alertLeadDays below. */
  if (f === "terminal") return t("taxonomies.col.terminal");
  /* Brief 87 §8b. No `fallback:` — seeded in all three languages, per the rule
     alertLeadDays states below. */
  if (f === "syncInTasksAndEvents") return t("taxonomies.col.syncInTasksAndEvents");
  /* Brief 123 §1. No `fallback:`, same rule. */
  if (f === "isMeeting") return t("taxonomies.col.isMeeting");
  /* 200 §E. No `fallback:`, same rule. */
  if (f === "secretarial") return t("taxonomies.col.secretarial");
  /* Brief 124 §1. No `fallback:`, same rule. */
  if (f === "defaultMode") return t("taxonomies.col.defaultMode");
  /* Brief 46 §1. No `fallback:` — this key is seeded in all three languages
     and gate 1's rule is that no new literal is written in any language,
     English included. The three above predate that rule and keep theirs. */
  if (f === "alertLeadDays") return t("taxonomies.col.alertLeadDays");
  /* 182 C.1. No `fallback:`, same rule. */
  if (f === "silenceDays") return t("taxonomies.col.silenceDays");
  return f;
}

// activity_code meta.eisen — the default Eisenhower quadrant for new tasks of
// this type. Four quadrants plus "unset" (no default → tasks land in "s").
// Labels come from the eisenhower_quadrant taxonomy via eisenLabel, so they
// follow the user's language like every other quadrant label in the app.
function EisenMetaSelect({ value, onChange, disabled, width = 150 }: any) {
  return (
    <MenuSelect
      className="field-input"
      style={inputStyle(width)}
      value={value || ""}
      onChange={(v) => onChange(v)}
      disabled={disabled}
      options={[
        ["", "—"] as const,
        ...EISEN_QUADRANTS.map(
          (q: any) => [String(q.k), eisenLabel(q.k)] as const,
        ),
      ]}
    />
  );
}

// P20b — event_type meta.firmWide. Ticked, every collaborator sees events of
// this type whatever the case scope says (server/src/lib/eventTypeScope.js is
// the predicate, on the GET and on the live stream alike). This is the one
// meta field on this screen that WIDENS who can read a row, so it writes a
// strict boolean: `true` or the key absent, never a truthy string.
function FirmWideMetaToggle({ value, onChange, disabled }: any) {
  return (
    <input
      type="checkbox"
      checked={value === true}
      onChange={(e) => onChange(e.target.checked ? true : undefined)}
      disabled={disabled}
      style={{ cursor: disabled ? "default" : "pointer" }}
    />
  );
}

// Brief 57 §1 — task_status meta.terminal. Ticked, a task carrying this status
// is OVER: it leaves the open count on the dashboard and on both case lists at
// once, because all three ask src/lib/taskCounters.ts and it asks this row.
//
// THREE STATES IN THE DATA, TWO ON THE SCREEN, and that is deliberate. A row
// that declares nothing falls back to the built-in terminal set
// (completed / cancelled / archived), which is what every row does until
// server/scripts/seed_migration_57_task_status_terminal.js has run — so the
// box shows the EFFECTIVE answer, resolved by the same function the app uses,
// and any click writes an EXPLICIT boolean. Unticking `completed` therefore
// records `false` rather than reverting to a fallback that would tick it
// straight back.
function TerminalMetaToggle({
  code,
  value,
  onChange,
  disabled,
}: {
  code: string;
  value: unknown;
  onChange: (v: boolean) => void;
  disabled: boolean;
}) {
  const effective = isTerminalStatus(code, [{ code, meta: { terminal: value } }]);
  return (
    <input
      type="checkbox"
      checked={effective}
      onChange={(e) => onChange(e.target.checked)}
      disabled={disabled}
      style={{ cursor: disabled ? "default" : "pointer" }}
    />
  );
}

// BRIEF 87 §8b — the "Sync in tasks and events" checkbox. A plain boolean on
// TaxonomyEntry.meta, which is a free `Json?`, so this needs NO schema change —
// the same convention `firmWide`, `terminal` and `eisen` already use.
//
// It is deliberately the SIMPLEST of the three toggles here: `terminal` derives
// an effective value through isTerminalStatus because a status can be terminal
// by name as well as by flag, and `firmWide` has its own component for the same
// reason. This one has no legacy: absent means false, and there is no code path
// that infers a sync from anything but this box.
function SyncStageMetaToggle({
  value,
  onChange,
  disabled,
}: {
  value: unknown;
  onChange: (v: boolean) => void;
  disabled: boolean;
}) {
  return (
    <input
      type="checkbox"
      checked={value === true}
      onChange={(e) => onChange(e.target.checked)}
      disabled={disabled}
      style={{ cursor: disabled ? "default" : "pointer" }}
    />
  );
}

// 200 §E [RULED — Laurent, 2026-09-20: "for other verticals it should not be
// hard coded"] — activity_code meta.secretarial. Ticked, a collaborator whose
// role bills at zero (the secretarial roles, `billsAtZeroCollab`) may log time
// on this activity: the time-entry modal restricts such a role to the ticked
// set, where App's `SEC_TASKS` literal used to name five legal codes (ADMIN ·
// CORR · EXCORR · PREP · TEL). The firm's own data now — any code, any number
// of them; nothing ticked means no restriction (the modal's stated rule). An
// explicit boolean, as isMeeting below. The hover on the box says what it does.
function SecretarialMetaToggle({
  value,
  onChange,
  disabled,
  t,
}: Readonly<{
  value: unknown;
  onChange: Dispatch<boolean>;
  disabled: boolean;
  t: Translate;
}>) {
  return (
    <input
      type="checkbox"
      checked={value === true}
      onChange={(e) => { onChange(e.target.checked); }}
      disabled={disabled}
      title={t("taxonomies.col.secretarial_title")}
      style={{ cursor: disabled ? "default" : "pointer" }}
    />
  );
}

// BRIEF 123 §1 — event_type meta.isMeeting. Ticked, events of this type ARE
// meetings: they get an end time, a Physical/Online choice on the event itself
// and an invitation list. ANY NUMBER OF TYPES MAY BE TICKED, which is the whole
// difference from `m365ImportRole` one arm above — that one routes an INCOMING
// M365 meeting and at most one row can hold each of its two roles; this one
// answers a question about the type itself, and a firm whose hearings are held
// by videoconference ticks AUD as well.
//
// AN EXPLICIT BOOLEAN, not `true | undefined` like `firmWide`, and the reason is
// the migration seed rather than taste: seed_migration_123_event_type_is_meeting
// declares the flag on every row carrying an m365ImportRole and SKIPS a row that
// already holds a boolean either way (57 §1's shape). Unticking REU therefore
// has to record `false`, or the next run of that seed would tick it back.
function IsMeetingMetaToggle({
  value,
  onChange,
  disabled,
}: {
  value: unknown;
  onChange: (v: boolean) => void;
  disabled: boolean;
}) {
  return (
    <input
      type="checkbox"
      checked={value === true}
      onChange={(e) => onChange(e.target.checked)}
      disabled={disabled}
      style={{ cursor: disabled ? "default" : "pointer" }}
    />
  );
}

// BRIEF 124 §1 — event_type meta.defaultMode. Which mode a NEW event of this
// type opens on: Physical, Online, or no preference.
//
// THREE OPTIONS AND THE FIRST IS NOT "PHYSICAL". Unset means the type declares
// nothing, and `meetingModeFor` (src/lib/meetingMode.ts) then supplies 123's
// conservative Physical. Collapsing the two would make "no preference" and
// "prefers Physical" the same row, and a firm could never say the first.
//
// IT IS A DEFAULT AND NEVER A LOCK: the event's own toggle stays editable
// whatever this says, which is the whole point of brief 123.
//
// Offered ONLY where `meta.isMeeting` is true — the caller gates it, because a
// mode is not a question a non-meeting type has an answer to. `MEETING_MODES`
// is imported rather than restated so the vocabulary has one home.
function DefaultModeMetaSelect({
  value,
  onChange,
  disabled,
}: {
  value: unknown;
  onChange: (v: MeetingMode | undefined) => void;
  disabled: boolean;
}) {
  const { t } = useTranslation();
  /* No `fallback:`, the rule `taxonomies.col.isMeeting` one arm above states:
     these are seeded in all three languages and gate 1 forbids a new literal in
     any of them, English included. */
  const LABELS: Record<MeetingMode, string> = {
    person: t("taxonomies.mode.person"),
    online: t("taxonomies.mode.online"),
  };
  return (
    <MenuSelect
      className="field-input"
      style={inputStyle(120)}
      value={asMeetingMode(value) || ""}
      onChange={(v) => onChange(asMeetingMode(v) || undefined)}
      disabled={disabled}
      options={[
        ["", "—"] as const,
        ...MEETING_MODES.map((m) => [String(m), LABELS[m]] as const),
      ]}
    />
  );
}

// P20c — event_type meta.m365ImportRole. Which type a meeting arriving from
// Microsoft 365 lands on: the online-meeting role, the physical-meeting role,
// or neither. At most one type holds each role (the resolver takes the first
// in taxonomy order), so tagging a second is a no-op rather than a surprise.
function M365RoleSelect({ value, onChange, disabled }: any) {
  const { t } = useTranslation();
  const LABELS: any = {
    virtual: t("taxonomies.m365.virtual", { fallback: "Online meeting" }),
    inPerson: t("taxonomies.m365.inPerson", { fallback: "In person" }),
  };
  return (
    <MenuSelect
      className="field-input"
      style={inputStyle(140)}
      value={value || ""}
      onChange={(v) => onChange(v || undefined)}
      disabled={disabled}
      options={[
        ["", "—"] as const,
        ...M365_IMPORT_ROLES.map((r: any) => [String(r), LABELS[r]] as const),
      ]}
    />
  );
}

// Brief 46 §1 — event_type meta.alertLeadDays. The days before an ALL-DAY
// event of this type on which it alerts; `PRE` ships with 60, 30, 14, 7, 1.
// This is the whole admin surface the slice adds: one field in the editor that
// already exists, per §4's instruction, and nothing else.
//
// A TEXT INPUT AND NOT A CHIP EDITOR, on purpose. The value is a short list of
// numbers an admin types once and revisits rarely; a bespoke widget would be a
// second thing to maintain for no gain, and this row has to sit in a table cell
// beside a colour swatch and two selects.
//
// It commits on BLUR, and it commits the NORMALISED value — the same
// `normaliseAlertLeadDays` the seed writes through — so what lands in the
// database is already canonical (whole days, no duplicates, furthest-out
// first) and the engine's reader never has to repair anything an admin typed.
// The input then re-displays what was actually stored rather than what was
// typed, which is how an admin discovers that "seven" was dropped: the field
// answers back.
//
// Empty commits `undefined`, which removes the key. ABSENCE IS NOT ZERO and
// this is where a firm expresses it — the M365RoleSelect idiom immediately
// below does the same for its own key.
type AlertLeadDaysFieldProps = {
  initialValue?: unknown;
  onCommit: (days: number[] | undefined) => void;
  disabled?: boolean;
  width?: number;
};
function AlertLeadDaysField({ initialValue, onCommit, disabled, width }: AlertLeadDaysFieldProps) {
  const asText = (v: unknown) =>
    Array.isArray(v) && v.length ? normaliseAlertLeadDays(v).join(", ") : "";
  const [val, setVal] = useState(asText(initialValue));
  return (
    <input
      className="field-input"
      style={inputStyle(width || 120)}
      value={val}
      disabled={disabled}
      inputMode="numeric"
      onChange={(e) => setVal(e.target.value)}
      onBlur={() => {
        const days = normaliseAlertLeadDays(val.split(","));
        setVal(days.join(", "));
        const before = normaliseAlertLeadDays(initialValue);
        if (days.join(",") !== before.join(","))
          onCommit(days.length ? days : undefined);
      }}
    />
  );
}

// 182 C.1 [RULED 2026-09-03, Laurent, 167 R8] — case_type meta.silenceDays:
// after this many days without a contact on a matter of this type, the alert
// engine tells the team (server/src/lib/alertEngine.js scanSilence). One
// numeric field in the editor that already exists, the AlertLeadDaysField
// shape one function up: it commits on BLUR, it commits the NORMALISED value
// (whole days in [1, 3650], the same rule the server's silenceDaysOf reads
// by), and empty commits `undefined`, which removes the key — ABSENCE IS NOT
// ZERO, and a type nobody configured never alarms.
interface SilenceDaysFieldProps {
  initialValue?: unknown;
  onCommit: (days: number | undefined) => void;
  disabled?: boolean;
  width?: number;
}
function SilenceDaysField({ initialValue, onCommit, disabled, width }: Readonly<SilenceDaysFieldProps>) {
  const asText = (v: unknown) => {
    const n = normaliseSilenceDays(v);
    return n === undefined ? "" : String(n);
  };
  const [val, setVal] = useState(asText(initialValue));
  return (
    <input
      className="field-input"
      style={inputStyle(width ?? 90)}
      value={val}
      disabled={disabled}
      inputMode="numeric"
      onChange={(e) => {
        setVal(e.target.value);
      }}
      onBlur={() => {
        const days = normaliseSilenceDays(val);
        setVal(days === undefined ? "" : String(days));
        if (days !== normaliseSilenceDays(initialValue)) onCommit(days);
      }}
    />
  );
}

// Phase 14 Stage 3 — icon meta field with a live Lucide preview. Scoped to
// the external_link kind (link_type keeps the plain text input). The typed string resolves via iconByName(); a name in ICON_NAMES
// renders its icon at full opacity, an unrecognised/typo'd name renders the
// MessageCircle fallback at reduced opacity as a "not found" signal. Plain
// text — no picker, no autocomplete; the curated registry is the implicit
// menu. Internal state drives the preview so it updates on every keystroke;
// callers pass onChange (immediate, used by the add form) or onCommit
// (on blur, used by the row to avoid an API patch per keystroke).
function IconMetaField({ initialValue, onChange, onCommit, disabled, width }: any) {
  const { t } = useTranslation();
  const [val, setVal] = useState(initialValue ?? "");
  const resolved = ICON_NAMES.includes(val);
  const Ico = iconByName(val);
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
      <input autoComplete="off" /* 207 §18 */
        className="field-input no-contact-autofill"
        style={inputStyle(width || 130)}
        placeholder={t("taxonomies.meta.icon_placeholder", {
          fallback: "Lucide name",
        })}
        value={val}
        onChange={(e) => {
          setVal(e.target.value);
          onChange && onChange(e.target.value);
        }}
        onBlur={() => {
          if (onCommit && val !== (initialValue ?? "")) onCommit(val);
        }}
        disabled={disabled}
      />
      <Ico
        size={16}
        title={
          resolved
            ? val
            : t("taxonomies.meta.icon_unknown", {
                fallback: "Icon name not recognised — showing fallback",
              })
        }
        style={{
          opacity: resolved ? 1 : 0.35,
          flexShrink: 0,
          color: "var(--ts)",
        }}
      />
    </span>
  );
}

// Phase 14 Stage 3 r3 — logo upload field for external_link entries. The
// admin picks an image file; FileReader turns it into a base64 data URI
// stored in meta.logoData (no server upload — the data URI rides in the
// taxonomy entry's JSON meta). Raster formats only (PNG / JPEG / WEBP):
// SVG is excluded because it can carry scripts (XSS vector). Files over
// LOGO_MAX_BYTES are rejected with a toast. The live preview mirrors
// ExternalView: the uploaded image when present, else the lucide icon
// (sibling meta.icon), else the MessageCircle fallback at reduced opacity.
// onChange receives the data URI on upload, or null on "Remove logo".
const LOGO_MAX_BYTES = 200000;
function LogoUploadField({ value, iconName, onChange, disabled, toast }: any) {
  const { t } = useTranslation();
  const [imgOk, setImgOk] = useState(false);
  const hasLogo = typeof value === "string" && value.startsWith("data:");
  // Re-probe whenever the stored image changes.
  useEffect(() => {
    setImgOk(false);
  }, [value]);
  const iconResolved = ICON_NAMES.includes(iconName);
  const Ico = iconByName(iconName);
  const showImg = hasLogo && imgOk;

  function pickFile(e: any) {
    const file = e.target.files && e.target.files[0];
    e.target.value = ""; // let the same file be re-picked after a removal
    if (!file) return;
    if (file.size > LOGO_MAX_BYTES) {
      toast &&
        toast(
          "⚠ " +
            t("taxonomies.meta.logoUpload_tooSlim", {
              fallback: "File too large (max 200 KB)",
            }),
        );
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === "string") onChange(reader.result);
    };
    reader.readAsDataURL(file);
  }

  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
      <label
        /* 190 round 5 [P] addendum (Laurent, 2026-09-16) — a file picker is
           not a deletion: the affirmative family, as the editor toggle. */
        className="btn btn-primary"
        style={{
          ...btnStyle,
          whiteSpace: "nowrap",
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.5 : 1,
        }}
      >
        {t("taxonomies.meta.logoUpload_placeholder", {
          fallback: "Upload logo",
        })}
        <input
          type="file"
          accept="image/png,image/jpeg,image/webp"
          onChange={pickFile}
          disabled={disabled}
          style={{ display: "none" }}
        />
      </label>
      {hasLogo && (
        <img
          key={value.slice(0, 48)}
          src={value}
          alt=""
          onLoad={() => setImgOk(true)}
          onError={() => setImgOk(false)}
          style={{
            width: 18,
            height: 18,
            objectFit: "contain",
            flexShrink: 0,
            display: showImg ? "block" : "none",
          }}
        />
      )}
      {!showImg && (
        <Ico
          size={16}
          style={{
            opacity: iconResolved ? 1 : 0.35,
            flexShrink: 0,
            color: "var(--ts)",
          }}
        />
      )}
      {hasLogo && (
        <button
          type="button"
          className="btn btn-danger"
          disabled={disabled}
          style={{ padding: "1px 5px", fontSize: FS.s9, whiteSpace: "nowrap" }}
          onClick={() => onChange(null)}
        >
          {t("taxonomies.meta.logoUpload_remove", { fallback: "Remove logo" })}
        </button>
      )}
    </span>
  );
}

const tdStyle = { padding: "3px 6px" };
const btnStyle = { padding: "2px 8px", fontSize: FS.s10 };
function inputStyle(width?: any) {
  return { padding: "2px 6px", fontSize: FS.s11, ...(width ? { width } : {}) };
}
function chevStyle(disabled: any) {
  return {
    background: "none",
    border: "none",
    padding: 0,
    lineHeight: 0,
    color: disabled ? "var(--br)" : "var(--tm)",
    cursor: disabled ? "default" : "pointer",
  };
}
